// src/screens/store/dashboard.jsx - Complete Store Dashboard
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ScreenHeader from "../../components/ScreenHeader";
import Pagination from "../../components/Pagination";
import {
  LogOut, PlusCircle, List, Calendar, UserPlus, Users,
  Eye, Edit, Trash2, FileText, FileSpreadsheet,
  X, ArrowLeft, ChevronRight, Search, Filter,
  Package, ShoppingCart, Truck, DollarSign, CreditCard,  
  CheckCircle, Clock, Mail, FolderPlus, Layers,
  UserCheck, Warehouse, Box, RefreshCw, AlertTriangle,
  RotateCcw, TrendingUp, ClipboardCheck, FileCheck
} from "lucide-react";
import "./dashboard.css";

const PAGE_SIZE = 10;

export default function StoreDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("create-ledger");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);

  const { fromRoute } = useSelector(state => state.navigation || { fromRoute: null });
  const cameFromMDDashboard = fromRoute === "/managing-director";

  // Sub-section states
  const [selectedLedgerSection, setSelectedLedgerSection] = useState(null);
  const [selectedProductSection, setSelectedProductSection] = useState(null);
  const [selectedStockType, setSelectedStockType] = useState(null);
  const [selectedPOFilter, setSelectedPOFilter] = useState(null);
  const [selectedSOFilter, setSelectedSOFilter] = useState(null);
  const [selectedInvoiceFilter, setSelectedInvoiceFilter] = useState(null);

  // Modal states
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [showCreateSupplier, setShowCreateSupplier] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showCreateSchedule, setShowCreateSchedule] = useState(false);
  const [showCreatePO, setShowCreatePO] = useState(false);
  const [showCreateSO, setShowCreateSO] = useState(false);
  const [showDispatch, setShowDispatch] = useState(false);
  const [showSupplierPayment, setShowSupplierPayment] = useState(false);
  const [showCustomerPayment, setShowCustomerPayment] = useState(false);
  const [showRawMaterialSelection, setShowRawMaterialSelection] = useState(false);
  const [showChildProduct, setShowChildProduct] = useState(false);

  // Validation attempt flags
  const [customerSubmitAttempted, setCustomerSubmitAttempted] = useState(false);
  const [supplierSubmitAttempted, setSupplierSubmitAttempted] = useState(false);
  const [categorySubmitAttempted, setCategorySubmitAttempted] = useState(false);
  const [productSubmitAttempted, setProductSubmitAttempted] = useState(false);
  const [groupSubmitAttempted, setGroupSubmitAttempted] = useState(false);
  const [scheduleSubmitAttempted, setScheduleSubmitAttempted] = useState(false);
  const [poSubmitAttempted, setPoSubmitAttempted] = useState(false);
  const [soSubmitAttempted, setSoSubmitAttempted] = useState(false);
  const [supplierPaymentSubmitAttempted, setSupplierPaymentSubmitAttempted] = useState(false);
  const [customerPaymentSubmitAttempted, setCustomerPaymentSubmitAttempted] = useState(false);
  const [dispatchSubmitAttempted, setDispatchSubmitAttempted] = useState(false);

  // Pagination states
  const [customerPage, setCustomerPage] = useState(1);
  const [supplierPage, setSupplierPage] = useState(1);
  const [productPage, setProductPage] = useState(1);
  const [categoryPage, setCategoryPage] = useState(1);
  const [groupPage, setGroupPage] = useState(1);
  const [schedulePage, setSchedulePage] = useState(1);
  const [poPage, setPoPage] = useState(1);
  const [soPage, setSoPage] = useState(1);
  const [grnPage, setGrnPage] = useState(1);
  const [invoicePage, setInvoicePage] = useState(1);

  // Data states
  const [customers, setCustomers] = useState([
    { id: 1, pocName: "John Doe", companyName: "ABC Industries", username: "john@abc.com", password: "pass123", address: "Chennai Main Road", pan: "ABCDE1234F", bankDetails: "HDFC Bank", gst: "22AAAAA0000A1Z", phone: "9876543210", vendorCode: "CUST-001" }
  ]);
  const [suppliers, setSuppliers] = useState([
    { id: 1, pocName: "SK", companyName: "SK Suppliers", username: "sk@suppliers.com", password: "pass456", address: "Coimbatore", pan: "BBBBB0000B", bankDetails: "ICICI Bank", gst: "33BBBBB0000B2Z", phone: "9876543211", supplierCode: "SUP-001" }
  ]);
  const [categories, setCategories] = useState([
    { id: 1, name: "Electronics", description: "Electronic components" },
    { id: 2, name: "Raw Material", description: "Raw materials for production" }
  ]);
  const [products, setProducts] = useState([
    { id: 1, name: "Keyboard", code: "KB-001", description: "Mechanical keyboard", category: "Electronics", dimension: "450x150mm", cost: 1500, stock: 100, rawMaterials: [{ name: "Plastic", quantity: 0.5 }, { name: "Metal", quantity: 0.3 }], childProducts: [] }
  ]);
  const [groups, setGroups] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([
    { id: 1, poNumber: "PO-001", customer: { companyName: "ABC Industries", id: 1 }, supplier: { companyName: "SK Suppliers", id: 1 }, orderDate: "2024-01-15", deliveryDate: "2024-01-30", totalAmount: 50000, status: "Pending", selectedProducts: [{ name: "Keyboard", quantity: 10, cost: 1500 }] }
  ]);
  const [saleOrders, setSaleOrders] = useState([
    { id: 1, soNumber: "SO-001", customer: { companyName: "ABC Industries", id: 1 }, orderDate: "2024-01-20", totalAmount: 15000, status: "Pending", selectedProducts: [{ name: "Keyboard", quantity: 10, cost: 1500 }] }
  ]);
  const [grns, setGrns] = useState([
    { id: 1, grnNumber: "GRN-001", poNumber: "PO-001", supplier: "SK Suppliers", product: "Keyboard", quantity: 10, supplierInvoiceNo: "INV-001", status: "Pending" }
  ]);
  const [invoices, setInvoices] = useState([
    { id: 1, invoiceNumber: "INV-001", customer: "ABC Industries", amount: 15000, cgst: 9, sgst: 9, igst: 0, status: "Pending", date: "2024-01-20" }
  ]);

  // Stock data - expanded as per Figma
  const [stockData, setStockData] = useState({
    rmStock: [{ id: 1, name: 'Plastic', quantity: 500 }, { id: 2, name: 'Metal', quantity: 300 }, { id: 3, name: 'Rubber', quantity: 200 }],
    finishedGoods: [{ id: 1, name: 'Keyboard', quantity: 100 }, { id: 2, name: 'Mouse', quantity: 150 }],
    productionStock: [{ id: 1, name: 'Keyboard', quantity: 50 }, { id: 2, name: 'Mouse', quantity: 30 }],
    salesReturnStock: [{ id: 1, name: 'Keyboard', quantity: 10 }, { id: 2, name: 'Mouse', quantity: 5 }],
    ncStock: [{ id: 1, name: 'Keyboard', quantity: 5 }],
    rejectionStock: [{ id: 1, name: 'Keyboard', quantity: 8 }],
    rwStock: [{ id: 1, name: 'Keyboard', quantity: 15 }],
    outsourcePending: [{ id: 1, name: 'Keyboard', quantity: 30 }]
  });

  // Form states
  const [customerForm, setCustomerForm] = useState({
    pocName: '', companyName: '', username: '', password: '',
    address: '', pan: '', bankDetails: '', gst: '', phone: '',
    vendorCode: 'CUST-' + Date.now()
  });
  const [supplierForm, setSupplierForm] = useState({
    pocName: '', companyName: '', username: '', password: '',
    address: '', pan: '', bankDetails: '', gst: '', phone: '',
    supplierCode: 'SUP-' + Date.now()
  });
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [productForm, setProductForm] = useState({
    name: '', code: '', description: '', category: '',
    dimension: '', extraDimension: '', totalDimension: '', cost: 0, stock: 0,
    rawMaterials: [], childProducts: [], image: null, model3d: null
  });
  const [groupForm, setGroupForm] = useState({ name: '', description: '', customers: [] });
  const [scheduleForm, setScheduleForm] = useState({
    category: '', subCategory: '', group: '', customer: '',
    productId: '', quantity: '', frequency: 'Weekly', validTill: ''
  });
  const [poForm, setPoForm] = useState({
    poNumber: 'PO-' + Date.now(), customerId: '', scheduleId: '',
    supplierId: '', orderDate: new Date().toISOString().split('T')[0],
    deliveryDate: '', selectedProducts: [], totalAmount: 0, status: 'Pending'
  });
  const [soForm, setSoForm] = useState({
    soNumber: 'SO-' + Date.now(), customerId: '', scheduleId: '',
    orderDate: new Date().toISOString().split('T')[0],
    selectedProducts: [], totalAmount: 0, status: 'Pending'
  });
  const [dispatchForm, setDispatchForm] = useState({
    customerId: '', scheduleId: '', selectedProducts: [],
    selectedScrap: [], selectedReturn: [], selectedNC: [],
    vehicleName: '', vehicleNumber: '', driverName: '', licenseValidity: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    paymentId: 'PAY-' + Date.now(), grnId: '', amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: 'Bank Transfer', utrNumber: '', verified: false
  });
  const [customerPaymentForm, setCustomerPaymentForm] = useState({
    paymentId: 'PAY-' + Date.now(), invoiceId: '', amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: 'Bank Transfer', utrNumber: '', cgst: 0, sgst: 0, igst: 0, verified: false
  });

  // Extra fields state
  const [extraFields, setExtraFields] = useState([]);
  const [longTextFields, setLongTextFields] = useState({
    description: '',
    terms: '',
    notes: ''
  });

  // Validation functions
  const validateCustomerForm = () => {
    const errors = {};
    if (!customerForm.pocName?.trim()) errors.pocName = 'POC Name is required';
    if (!customerForm.companyName?.trim()) errors.companyName = 'Company Name is required';
    if (!customerForm.username?.trim()) errors.username = 'Username is required';
    if (!customerForm.password?.trim()) errors.password = 'Password is required';
    if (!customerForm.address?.trim()) errors.address = 'Address is required';
    return errors;
  };

  const RegularButton = ({ children, onClick, className, title }) => (
    <button className={className} onClick={onClick} title={title}>
      {children}
    </button>
  );

  const validateSupplierForm = () => {
    const errors = {};
    if (!supplierForm.pocName?.trim()) errors.pocName = 'POC Name is required';
    if (!supplierForm.companyName?.trim()) errors.companyName = 'Company Name is required';
    if (!supplierForm.username?.trim()) errors.username = 'Username is required';
    if (!supplierForm.password?.trim()) errors.password = 'Password is required';
    if (!supplierForm.address?.trim()) errors.address = 'Address is required';
    return errors;
  };

  const validateCategoryForm = () => {
    const errors = {};
    if (!categoryForm.name?.trim()) errors.name = 'Category Name is required';
    return errors;
  };

  const validateProductForm = () => {
    const errors = {};
    if (!productForm.name?.trim()) errors.name = 'Product Name is required';
    if (!productForm.code?.trim()) errors.code = 'Product Code is required';
    if (!productForm.category) errors.category = 'Category is required';
    if (!productForm.cost || productForm.cost <= 0) errors.cost = 'Valid Cost is required';
    return errors;
  };

  const validateGroupForm = () => {
    const errors = {};
    if (!groupForm.name?.trim()) errors.name = 'Group Name is required';
    return errors;
  };

  const validateScheduleForm = () => {
    const errors = {};
    if (!scheduleForm.category) errors.category = 'Category is required';
    if (!scheduleForm.customer) errors.customer = 'Customer is required';
    if (!scheduleForm.productId) errors.productId = 'Product is required';
    if (!scheduleForm.quantity || scheduleForm.quantity <= 0) errors.quantity = 'Valid Quantity is required';
    if (!scheduleForm.validTill) errors.validTill = 'Valid Till date is required';
    return errors;
  };

  const validatePOForm = () => {
    const errors = {};
    if (!poForm.customerId) errors.customerId = 'Customer is required';
    if (!poForm.supplierId) errors.supplierId = 'Supplier is required';
    if (!poForm.deliveryDate) errors.deliveryDate = 'Delivery Date is required';
    if (poForm.selectedProducts.length === 0) errors.selectedProducts = 'At least one product is required';
    return errors;
  };

  const validateSOForm = () => {
    const errors = {};
    if (!soForm.customerId) errors.customerId = 'Customer is required';
    if (soForm.selectedProducts.length === 0) errors.selectedProducts = 'At least one product is required';
    return errors;
  };

  const validateDispatchForm = () => {
    const errors = {};
    if (!dispatchForm.customerId) errors.customerId = 'Customer is required';
    if (!dispatchForm.vehicleName?.trim()) errors.vehicleName = 'Vehicle Name is required';
    if (!dispatchForm.vehicleNumber?.trim()) errors.vehicleNumber = 'Vehicle Number is required';
    if (!dispatchForm.driverName?.trim()) errors.driverName = 'Driver Name is required';
    return errors;
  };

  const validatePaymentForm = () => {
    const errors = {};
    if (!paymentForm.grnId) errors.grnId = 'GRN is required';
    if (!paymentForm.amount || paymentForm.amount <= 0) errors.amount = 'Valid Amount is required';
    if (!paymentForm.utrNumber?.trim()) errors.utrNumber = 'UTR Number is required';
    return errors;
  };

  const validateCustomerPaymentForm = () => {
    const errors = {};
    if (!customerPaymentForm.invoiceId) errors.invoiceId = 'Invoice is required';
    if (!customerPaymentForm.amount || customerPaymentForm.amount <= 0) errors.amount = 'Valid Amount is required';
    if (!customerPaymentForm.utrNumber?.trim()) errors.utrNumber = 'UTR Number is required';
    return errors;
  };

  // Error getters
  const getCustomerError = (field) => {
    if (!customerSubmitAttempted) return null;
    return validateCustomerForm()[field];
  };

  const getSupplierError = (field) => {
    if (!supplierSubmitAttempted) return null;
    return validateSupplierForm()[field];
  };

  const getCategoryError = (field) => {
    if (!categorySubmitAttempted) return null;
    return validateCategoryForm()[field];
  };

  const getProductError = (field) => {
    if (!productSubmitAttempted) return null;
    return validateProductForm()[field];
  };

  const getGroupError = (field) => {
    if (!groupSubmitAttempted) return null;
    return validateGroupForm()[field];
  };

  const getScheduleError = (field) => {
    if (!scheduleSubmitAttempted) return null;
    return validateScheduleForm()[field];
  };

  const getPOError = (field) => {
    if (!poSubmitAttempted) return null;
    return validatePOForm()[field];
  };

  const getSOError = (field) => {
    if (!soSubmitAttempted) return null;
    return validateSOForm()[field];
  };

  const getDispatchError = (field) => {
    if (!dispatchSubmitAttempted) return null;
    return validateDispatchForm()[field];
  };

  const getPaymentError = (field) => {
    if (!supplierPaymentSubmitAttempted) return null;
    return validatePaymentForm()[field];
  };

  const getCustomerPaymentError = (field) => {
    if (!customerPaymentSubmitAttempted) return null;
    return validateCustomerPaymentForm()[field];
  };

  // Submit handlers
  const handleCreateCustomer = () => {
    setCustomerSubmitAttempted(true);
    if (Object.keys(validateCustomerForm()).length > 0) return;
    const newCustomer = { id: Date.now(), ...customerForm, createdAt: new Date().toISOString() };
    setCustomers([...customers, newCustomer]);
    setShowCreateCustomer(false);
    setCustomerSubmitAttempted(false);
    setCustomerForm({
      pocName: '', companyName: '', username: '', password: '',
      address: '', pan: '', bankDetails: '', gst: '', phone: '',
      vendorCode: 'CUST-' + Date.now()
    });
    alert('Customer created successfully!');
  };

  const handleCreateSupplier = () => {
    setSupplierSubmitAttempted(true);
    if (Object.keys(validateSupplierForm()).length > 0) return;
    const newSupplier = { id: Date.now(), ...supplierForm, createdAt: new Date().toISOString() };
    setSuppliers([...suppliers, newSupplier]);
    setShowCreateSupplier(false);
    setSupplierSubmitAttempted(false);
    setSupplierForm({
      pocName: '', companyName: '', username: '', password: '',
      address: '', pan: '', bankDetails: '', gst: '', phone: '',
      supplierCode: 'SUP-' + Date.now()
    });
    alert('Supplier created successfully!');
  };

  const handleCreateCategory = () => {
    setCategorySubmitAttempted(true);
    if (Object.keys(validateCategoryForm()).length > 0) return;
    const newCategory = { id: Date.now(), name: categoryForm.name, description: categoryForm.description || 'N/A' };
    setCategories([...categories, newCategory]);
    setShowCreateCategory(false);
    setCategorySubmitAttempted(false);
    setCategoryForm({ name: '', description: '' });
    alert('Category created successfully!');
  };

  const handleCreateProduct = () => {
    setProductSubmitAttempted(true);
    if (Object.keys(validateProductForm()).length > 0) return;
    const newProduct = {
      id: Date.now(), ...productForm,
      totalDimension: productForm.totalDimension || 
        (parseFloat(productForm.dimension) + parseFloat(productForm.extraDimension || 0)).toString(),
      createdAt: new Date().toISOString(),
      extraFields: extraFields,
      longTextFields: longTextFields
    };
    setProducts([...products, newProduct]);
    setShowCreateProduct(false);
    setProductSubmitAttempted(false);
    setProductForm({
      name: '', code: '', description: '', category: '',
      dimension: '', extraDimension: '', totalDimension: '', cost: 0, stock: 0,
      rawMaterials: [], childProducts: [], image: null, model3d: null
    });
    setExtraFields([]);
    setLongTextFields({ description: '', terms: '', notes: '' });
    alert('Product created successfully!');
  };

  const handleCreateGroup = () => {
    setGroupSubmitAttempted(true);
    if (Object.keys(validateGroupForm()).length > 0) return;
    const selectedCustomers = customers.filter(c => groupForm.customers.includes(c.id));
    const newGroup = { id: Date.now(), name: groupForm.name, description: groupForm.description || 'N/A', customers: selectedCustomers };
    setGroups([...groups, newGroup]);
    setShowCreateGroup(false);
    setGroupSubmitAttempted(false);
    setGroupForm({ name: '', description: '', customers: [] });
    alert('Group created successfully!');
  };

  const handleCreateSchedule = () => {
    setScheduleSubmitAttempted(true);
    if (Object.keys(validateScheduleForm()).length > 0) return;
    const selectedProduct = products.find(p => p.id === parseInt(scheduleForm.productId));
    const newSchedule = {
      id: Date.now(), ...scheduleForm,
      product: selectedProduct,
      productName: selectedProduct?.name || 'N/A',
      quantity: parseInt(scheduleForm.quantity),
      createdAt: new Date().toISOString()
    };
    setSchedules([...schedules, newSchedule]);
    setShowCreateSchedule(false);
    setScheduleSubmitAttempted(false);
    setScheduleForm({
      category: '', subCategory: '', group: '', customer: '',
      productId: '', quantity: '', frequency: 'Weekly', validTill: ''
    });
    alert('Schedule created successfully!');
  };

  const handleCreatePO = () => {
    setPoSubmitAttempted(true);
    if (Object.keys(validatePOForm()).length > 0) return;
    const selectedCustomer = customers.find(c => c.id === parseInt(poForm.customerId));
    const selectedSupplier = suppliers.find(s => s.id === parseInt(poForm.supplierId));
    const newPO = {
      id: Date.now(),
      poNumber: poForm.poNumber,
      customer: selectedCustomer,
      supplier: selectedSupplier,
      selectedProducts: poForm.selectedProducts,
      orderDate: poForm.orderDate,
      deliveryDate: poForm.deliveryDate,
      totalAmount: poForm.totalAmount,
      status: 'Pending',
      createdAt: new Date().toISOString()
    };
    setPurchaseOrders([...purchaseOrders, newPO]);
    setShowCreatePO(false);
    setPoSubmitAttempted(false);
    setPoForm({
      poNumber: 'PO-' + Date.now(), customerId: '', scheduleId: '',
      supplierId: '', orderDate: new Date().toISOString().split('T')[0],
      deliveryDate: '', selectedProducts: [], totalAmount: 0, status: 'Pending'
    });
    alert('Purchase Order created successfully!');
  };

  const handleCreateSO = () => {
    setSoSubmitAttempted(true);
    if (Object.keys(validateSOForm()).length > 0) return;
    const selectedCustomer = customers.find(c => c.id === parseInt(soForm.customerId));
    const newSO = {
      id: Date.now(),
      soNumber: soForm.soNumber,
      customer: selectedCustomer,
      selectedProducts: soForm.selectedProducts,
      orderDate: soForm.orderDate,
      totalAmount: soForm.totalAmount,
      status: 'Pending',
      createdAt: new Date().toISOString()
    };
    setSaleOrders([...saleOrders, newSO]);
    setShowCreateSO(false);
    setSoSubmitAttempted(false);
    setSoForm({
      soNumber: 'SO-' + Date.now(), customerId: '', scheduleId: '',
      orderDate: new Date().toISOString().split('T')[0],
      selectedProducts: [], totalAmount: 0, status: 'Pending'
    });
    alert('Sale Order created successfully!');
  };

  const handleDispatch = () => {
    setDispatchSubmitAttempted(true);
    if (Object.keys(validateDispatchForm()).length > 0) return;
    // Update stock quantities
    const updatedStock = { ...stockData };
    dispatchForm.selectedProducts.forEach(product => {
      const fgItem = updatedStock.finishedGoods.find(item => item.name === product.name);
      if (fgItem) fgItem.quantity -= product.quantity;
    });
    setStockData(updatedStock);
    setShowDispatch(false);
    setDispatchSubmitAttempted(false);
    setDispatchForm({
      customerId: '', scheduleId: '', selectedProducts: [],
      selectedScrap: [], selectedReturn: [], selectedNC: [],
      vehicleName: '', vehicleNumber: '', driverName: '', licenseValidity: ''
    });
    alert('Dispatch completed successfully!');
  };

  const handleSupplierPayment = () => {
    setSupplierPaymentSubmitAttempted(true);
    if (Object.keys(validatePaymentForm()).length > 0) return;
    setShowSupplierPayment(false);
    setSupplierPaymentSubmitAttempted(false);
    setPaymentForm({
      paymentId: 'PAY-' + Date.now(), grnId: '', amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMode: 'Bank Transfer', utrNumber: '', verified: false
    });
    alert('Supplier Payment recorded successfully!');
  };

  const handleCustomerPayment = () => {
    setCustomerPaymentSubmitAttempted(true);
    if (Object.keys(validateCustomerPaymentForm()).length > 0) return;
    // Update invoice status
    const updatedInvoices = invoices.map(inv => 
      inv.id === parseInt(customerPaymentForm.invoiceId) ? { ...inv, status: 'Paid' } : inv
    );
    setInvoices(updatedInvoices);
    setShowCustomerPayment(false);
    setCustomerPaymentSubmitAttempted(false);
    setCustomerPaymentForm({
      paymentId: 'PAY-' + Date.now(), invoiceId: '', amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMode: 'Bank Transfer', utrNumber: '', cgst: 0, sgst: 0, igst: 0, verified: false
    });
    alert('Customer Payment recorded successfully!');
  };

  const handleApprovePO = (poId) => {
    const updatedOrders = purchaseOrders.map(po => 
      po.id === poId ? { ...po, status: 'Approved' } : po
    );
    setPurchaseOrders(updatedOrders);
    alert('Purchase Order approved successfully!');
  };

  const handleApproveSO = (soId) => {
    const updatedOrders = saleOrders.map(so => 
      so.id === soId ? { ...so, status: 'Approved' } : so
    );
    setSaleOrders(updatedOrders);
    alert('Sale Order approved successfully!');
  };

  const handleAddProductToPO = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product && !poForm.selectedProducts.find(p => p.id === productId)) {
      setPoForm({
        ...poForm,
        selectedProducts: [...poForm.selectedProducts, { ...product, quantity: 1 }],
        totalAmount: poForm.totalAmount + (product.cost || 0)
      });
    }
  };

  const handleUpdateProductQuantity = (productId, quantity, type) => {
    if (type === 'po') {
      const updatedProducts = poForm.selectedProducts.map(p =>
        p.id === productId ? { ...p, quantity: parseInt(quantity) || 0 } : p
      );
      const total = updatedProducts.reduce((sum, p) => sum + ((p.cost || 0) * (p.quantity || 0)), 0);
      setPoForm({ ...poForm, selectedProducts: updatedProducts, totalAmount: total });
    } else if (type === 'so') {
      const updatedProducts = soForm.selectedProducts.map(p =>
        p.id === productId ? { ...p, quantity: parseInt(quantity) || 0 } : p
      );
      const total = updatedProducts.reduce((sum, p) => sum + ((p.cost || 0) * (p.quantity || 0)), 0);
      setSoForm({ ...soForm, selectedProducts: updatedProducts, totalAmount: total });
    } else if (type === 'dispatch') {
      const updatedProducts = dispatchForm.selectedProducts.map(p =>
        p.id === productId ? { ...p, quantity: parseInt(quantity) || 0 } : p
      );
      setDispatchForm({ ...dispatchForm, selectedProducts: updatedProducts });
    }
  };

  const handleAddProductToSO = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product && !soForm.selectedProducts.find(p => p.id === productId)) {
      setSoForm({
        ...soForm,
        selectedProducts: [...soForm.selectedProducts, { ...product, quantity: 1 }],
        totalAmount: soForm.totalAmount + (product.cost || 0)
      });
    }
  };

  const handleAddProductToDispatch = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product && !dispatchForm.selectedProducts.find(p => p.id === productId)) {
      setDispatchForm({
        ...dispatchForm,
        selectedProducts: [...dispatchForm.selectedProducts, { ...product, quantity: 1 }]
      });
    }
  };

  const handleRemoveProduct = (type, productId) => {
    if (type === 'po') {
      const updatedProducts = poForm.selectedProducts.filter(p => p.id !== productId);
      const total = updatedProducts.reduce((sum, p) => sum + ((p.cost || 0) * (p.quantity || 0)), 0);
      setPoForm({ ...poForm, selectedProducts: updatedProducts, totalAmount: total });
    } else if (type === 'so') {
      const updatedProducts = soForm.selectedProducts.filter(p => p.id !== productId);
      const total = updatedProducts.reduce((sum, p) => sum + ((p.cost || 0) * (p.quantity || 0)), 0);
      setSoForm({ ...soForm, selectedProducts: updatedProducts, totalAmount: total });
    } else if (type === 'dispatch') {
      const updatedProducts = dispatchForm.selectedProducts.filter(p => p.id !== productId);
      setDispatchForm({ ...dispatchForm, selectedProducts: updatedProducts });
    }
  };

  // Extra fields handlers
  const handleAddExtraField = () => {
    setExtraFields([...extraFields, { key: '', value: '' }]);
  };

  const handleRemoveExtraField = (index) => {
    const newFields = [...extraFields];
    newFields.splice(index, 1);
    setExtraFields(newFields);
  };

  const handleExtraFieldChange = (index, field, value) => {
    const newFields = [...extraFields];
    newFields[index][field] = value;
    setExtraFields(newFields);
  };

  const handleLongTextFieldChange = (field, value) => {
    setLongTextFields({ ...longTextFields, [field]: value });
  };

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    onLogout();
    navigate('/login');
  };
  const handleCancelLogout = () => setShowLogoutModal(false);
  const handleBack = () => {
    setSelectedLedgerSection(null);
    setSelectedProductSection(null);
    setSelectedStockType(null);
    setSelectedPOFilter(null);
    setSelectedSOFilter(null);
    setSelectedInvoiceFilter(null);
  };
  const handleBackToMD = () => navigate("/managing-director");

  // Helper functions
  const formatValue = (value) => {
    if (value === undefined || value === null || value === '') return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value);
    return value;
  };

  const filteredData = (data, searchFields) => {
    if (!searchTerm) return data;
    return data.filter(item =>
      searchFields.some(field => {
        const value = item[field];
        if (value === undefined || value === null) return false;
        return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  };

  const paginate = (data, page) => data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ActionIconButton with Tooltip
  const ActionIconButton = ({ icon, label, onClick, colorClass }) => (
    <button className={`action-btn ${colorClass}`} onClick={onClick} style={{ position: 'relative' }}>
      {icon}
      <span className="tooltip-text">{label}</span>
    </button>
  );

  // Sidebar menu items
  const tabItems = [
    { id: "create-ledger", label: "Create Ledger", icon: <FileText size={18} /> },
    { id: "product", label: "Product", icon: <Package size={18} /> },
    { id: "create-schedule", label: "Create Schedule", icon: <Calendar size={18} /> },
    { id: "create-po", label: "Create PO", icon: <ShoppingCart size={18} /> },
    { id: "list-po", label: "List Purchase Order", icon: <List size={18} /> },
    { id: "create-sale-order", label: "Create Sale Order", icon: <ShoppingCart size={18} /> },
    { id: "list-sale-order", label: "List Sale Order", icon: <List size={18} /> },
    { id: "dispatch", label: "Dispatch", icon: <Truck size={18} /> },
    { id: "list-grn", label: "List GRN", icon: <FileCheck size={18} /> },
    { id: "list-invoices", label: "List Invoices", icon: <FileText size={18} /> },
    { id: "supplier-payment", label: "Supplier Payment", icon: <DollarSign size={18} /> },
    { id: "customer-payment", label: "Customer Payment", icon: <CreditCard size={18} /> }
  ];

  // Stock sections as per Figma
  const stockSections = [
    { key: "rmStock", label: "RM Stock", icon: <Package size={20} /> },
    { key: "finishedGoods", label: "FG Stock", icon: <CheckCircle size={20} /> },
    { key: "productionStock", label: "Production Stock", icon: <TrendingUp size={20} /> },
    { key: "salesReturnStock", label: "Sales return Stock", icon: <RotateCcw size={20} /> },
    { key: "ncStock", label: "NC Stock", icon: <AlertTriangle size={20} /> },
    { key: "rejectionStock", label: "Rejection Stock", icon: <X size={20} /> },
    { key: "rwStock", label: "RW Stock", icon: <RefreshCw size={20} /> },
    { key: "outsourcePending", label: "Outsource Pending", icon: <Truck size={20} /> }
  ];

  // ==================== RENDER FUNCTIONS ====================

  const renderCreateLedger = () => {
    // Data for tables
    const filteredCustomers = filteredData(customers, ['companyName', 'pocName', 'username']);
    const filteredSuppliers = filteredData(suppliers, ['companyName', 'pocName', 'username']);
    const totalCustomerPages = Math.ceil(filteredCustomers.length / PAGE_SIZE);
    const totalSupplierPages = Math.ceil(filteredSuppliers.length / PAGE_SIZE);
    const customerPageData = paginate(filteredCustomers, customerPage);
    const supplierPageData = paginate(filteredSuppliers, supplierPage);
  
    return (
      <div>
        {/* Two Cards Side by Side - Like Purchase Dashboard */}
        <div className="create-cards-grid">
          <div className="create-card" onClick={() => setShowCreateCustomer(true)}>
            <div className="create-card-icon"><UserPlus size={28} /></div>
            <h3>Create Customer</h3>
            <p>Add new customer to the system with complete details</p>
            <button className="create-card-btn"><PlusCircle size={16} /> Create Customer</button>
          </div>
  
          <div className="create-card" onClick={() => setShowCreateSupplier(true)} style={{ background: "linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)" }}>
            <div className="create-card-icon"><Truck size={28} /></div>
            <h3>Create Supplier</h3>
            <p>Add new supplier to the system with complete details</p>
            <button className="create-card-btn"><PlusCircle size={16} /> Create Supplier</button>
          </div>
        </div>
  
        {/* List Customer Section */}
        <div className="list-section">
          <h3><Users size={18} /> List Customer ({customers.length})</h3>
          {customers.length === 0 ? (
            <div className="empty-state"><p>No customers yet. Create a customer to see it here.</p></div>
          ) : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>POC Name</th><th>Company Name</th><th>Username</th><th>Phone</th><th>GST</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerPageData.map(customer => (
                      <tr key={customer.id}>
                        <td className="highlight">{formatValue(customer.pocName)}</td>
                        <td>{formatValue(customer.companyName)}</td>
                        <td>{formatValue(customer.username)}</td>
                        <td>{formatValue(customer.phone)}</td>
                        <td>{formatValue(customer.gst)}</td>
                        <td className="action-buttons">
                          <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(customer, null, 2))} colorClass="view" />
                          <ActionIconButton icon={<Edit size={16} />} label="Edit Customer" onClick={() => alert('Edit customer: ' + customer.companyName)} colorClass="edit" />
                          <ActionIconButton icon={<Trash2 size={16} />} label="Delete Customer" onClick={() => { if (window.confirm('Delete?')) setCustomers(customers.filter(c => c.id !== customer.id)); }} colorClass="delete" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {customers.length > PAGE_SIZE && (
                <div className="pagination-container">
                  <div className="pagination-summary">Showing {Math.min(customerPage * PAGE_SIZE, filteredCustomers.length)} of {filteredCustomers.length} entries</div>
                  <div className="pagination-buttons">
                    <button className="pagination-button" onClick={() => setCustomerPage(p => Math.max(1, p - 1))} disabled={customerPage === 1}>Prev</button>
                    <span className="pagination-button active">{customerPage}</span>
                    <button className="pagination-button" onClick={() => setCustomerPage(p => p + 1)} disabled={customerPage * PAGE_SIZE >= filteredCustomers.length}>Next</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
  
        {/* List Supplier Section */}
        <div className="list-section">
          <h3><Truck size={18} /> List Supplier ({suppliers.length})</h3>
          {suppliers.length === 0 ? (
            <div className="empty-state"><p>No suppliers yet. Create a supplier to see it here.</p></div>
          ) : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>POC Name</th><th>Company Name</th><th>Username</th><th>Phone</th><th>GST</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supplierPageData.map(supplier => (
                      <tr key={supplier.id}>
                        <td className="highlight">{formatValue(supplier.pocName)}</td>
                        <td>{formatValue(supplier.companyName)}</td>
                        <td>{formatValue(supplier.username)}</td>
                        <td>{formatValue(supplier.phone)}</td>
                        <td>{formatValue(supplier.gst)}</td>
                        <td className="action-buttons">
                          <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(supplier, null, 2))} colorClass="view" />
                          <ActionIconButton icon={<Edit size={16} />} label="Edit Supplier" onClick={() => alert('Edit supplier: ' + supplier.companyName)} colorClass="edit" />
                          <ActionIconButton icon={<Trash2 size={16} />} label="Delete Supplier" onClick={() => { if (window.confirm('Delete?')) setSuppliers(suppliers.filter(s => s.id !== supplier.id)); }} colorClass="delete" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {suppliers.length > PAGE_SIZE && (
                <div className="pagination-container">
                  <div className="pagination-summary">Showing {Math.min(supplierPage * PAGE_SIZE, filteredSuppliers.length)} of {filteredSuppliers.length} entries</div>
                  <div className="pagination-buttons">
                    <button className="pagination-button" onClick={() => setSupplierPage(p => Math.max(1, p - 1))} disabled={supplierPage === 1}>Prev</button>
                    <span className="pagination-button active">{supplierPage}</span>
                    <button className="pagination-button" onClick={() => setSupplierPage(p => p + 1)} disabled={supplierPage * PAGE_SIZE >= filteredSuppliers.length}>Next</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderProduct = () => {
    if (selectedStockType) {
      const stockItems = stockData[selectedStockType] || [];
      const titles = {
        rmStock: 'RM Stock (Raw Material)',
        finishedGoods: 'FG Stock (Finished Goods)',
        productionStock: 'Production Stock',
        salesReturnStock: 'Sales Return Stock',
        ncStock: 'NC Stock (Non-Conforming)',
        rejectionStock: 'Rejection Stock',
        rwStock: 'RW Stock (Rework)',
        outsourcePending: 'Outsource Pending Stock'
      };
      return (
        <div>
          <div className="back-header">
            <button className="back-btn" onClick={() => setSelectedStockType(null)} title="Back"><ArrowLeft size={18} /> Back</button>
            <h2 className="section-title">{titles[selectedStockType]}</h2>
          </div>
          {stockItems.length === 0 ? (
            <div className="empty-state"><Package size={48} color="#CBD5E1" /><p>No items in this stock</p></div>
          ) : (
            <div className="stock-list-container">
              {stockItems.map((item, idx) => (
                <div key={idx} className="stock-item">
                  <span><strong>{formatValue(item.name)}</strong></span>
                  <span className="stock-quantity">Quantity: {formatValue(item.quantity)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
  
    // Filter data for display
    const filteredCategories = filteredData(categories, ['name']);
    const filteredProducts = filteredData(products, ['name', 'code']);
    const filteredGroups = filteredData(groups, ['name']);
    
    const totalCategoryPages = Math.ceil(filteredCategories.length / PAGE_SIZE);
    const totalProductPages = Math.ceil(filteredProducts.length / PAGE_SIZE);
    const totalGroupPages = Math.ceil(filteredGroups.length / PAGE_SIZE);
    
    const categoryPageData = paginate(filteredCategories, categoryPage);
    const productPageData = paginate(filteredProducts, productPage);
    const groupPageData = paginate(filteredGroups, groupPage);
  
    return (
      <div>
        <h2 className="section-title">Product Management</h2>
        
        {/* Create Cards Grid - Only Create Buttons */}
        <div className="create-cards-grid">
          <div className="create-card" onClick={() => setShowCreateCategory(true)}>
            <div className="create-card-icon"><FolderPlus size={28} /></div>
            <h3>Create Category</h3>
            <p>Add new product category</p>
            <button className="create-card-btn"><PlusCircle size={16} /> Create Category</button>
          </div>
  
          <div className="create-card" onClick={() => setShowCreateProduct(true)} style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}>
            <div className="create-card-icon"><Package size={28} /></div>
            <h3>Create Product</h3>
            <p>Add new product with details, child products & raw materials</p>
            <button className="create-card-btn"><PlusCircle size={16} /> Create Product</button>
          </div>
  
          <div className="create-card" onClick={() => setShowCreateGroup(true)} style={{ background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" }}>
            <div className="create-card-icon"><Layers size={28} /></div>
            <h3>Create Group</h3>
            <p>Group products together</p>
            <button className="create-card-btn"><PlusCircle size={16} /> Create Group</button>
          </div>
        </div>
  
        {/* List Category Section */}
        <div className="list-section">
          <h3><FolderPlus size={18} /> List Category ({categories.length})</h3>
          {categories.length === 0 ? (
            <div className="empty-state"><p>No categories yet. Create a category to see it here.</p></div>
          ) : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Category Name</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryPageData.map(cat => (
                      <tr key={cat.id}>
                        <td className="highlight">{formatValue(cat.name)}</td>
                        <td>{formatValue(cat.description)}</td>
                        <td className="action-buttons">
                          <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(`Category: ${cat.name}\nDescription: ${cat.description}`)} colorClass="view" />
                          <ActionIconButton icon={<Edit size={16} />} label="Edit Category" onClick={() => alert('Edit category: ' + cat.name)} colorClass="edit" />
                          <ActionIconButton icon={<Trash2 size={16} />} label="Delete Category" onClick={() => { if (window.confirm('Delete this category?')) setCategories(categories.filter(c => c.id !== cat.id)); }} colorClass="delete" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {categories.length > PAGE_SIZE && (
                <div className="pagination-container">
                  <div className="pagination-summary">Showing {Math.min(categoryPage * PAGE_SIZE, filteredCategories.length)} of {filteredCategories.length} entries</div>
                  <div className="pagination-buttons">
                    <button className="pagination-button" onClick={() => setCategoryPage(p => Math.max(1, p - 1))} disabled={categoryPage === 1}>Prev</button>
                    <span className="pagination-button active">{categoryPage}</span>
                    <button className="pagination-button" onClick={() => setCategoryPage(p => p + 1)} disabled={categoryPage * PAGE_SIZE >= filteredCategories.length}>Next</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
  
        {/* List Product Section */}
        <div className="list-section">
          <h3><Package size={18} /> List Product ({products.length})</h3>
          {products.length === 0 ? (
            <div className="empty-state"><p>No products yet. Create a product to see it here.</p></div>
          ) : (
            <>
              <div className="filter-bar">
                <Search size={18} />
                <input type="text" placeholder="Search products..." className="filter-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>Code</th>
                      <th>Category</th>
                      <th>Dimension</th>
                      <th>Cost</th>
                      <th>Stock</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productPageData.map(prod => (
                      <tr key={prod.id}>
                        <td className="highlight">{formatValue(prod.name)}</td>
                        <td>{formatValue(prod.code)}</td>
                        <td>{formatValue(prod.category)}</td>
                        <td>{formatValue(prod.dimension)}</td>
                        <td className="amount">₹{formatValue(prod.cost)}</td>
                        <td>{formatValue(prod.stock)}</td>
                        <td className="action-buttons">
                          <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(prod, null, 2))} colorClass="view" />
                          <ActionIconButton icon={<Edit size={16} />} label="Edit Product" onClick={() => alert('Edit product: ' + prod.name)} colorClass="edit" />
                          <ActionIconButton icon={<Trash2 size={16} />} label="Delete Product" onClick={() => { if (window.confirm('Delete this product?')) setProducts(products.filter(p => p.id !== prod.id)); }} colorClass="delete" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {products.length > PAGE_SIZE && (
                <div className="pagination-container">
                  <div className="pagination-summary">Showing {Math.min(productPage * PAGE_SIZE, filteredProducts.length)} of {filteredProducts.length} entries</div>
                  <div className="pagination-buttons">
                    <button className="pagination-button" onClick={() => setProductPage(p => Math.max(1, p - 1))} disabled={productPage === 1}>Prev</button>
                    <span className="pagination-button active">{productPage}</span>
                    <button className="pagination-button" onClick={() => setProductPage(p => p + 1)} disabled={productPage * PAGE_SIZE >= filteredProducts.length}>Next</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
  
        {/* List Group Section */}
        <div className="list-section">
          <h3><Layers size={18} /> List Group ({groups.length})</h3>
          {groups.length === 0 ? (
            <div className="empty-state"><p>No groups yet. Create a group to see it here.</p></div>
          ) : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Group Name</th>
                      <th>Description</th>
                      <th>Products/Customers</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupPageData.map(group => (
                      <tr key={group.id}>
                        <td className="highlight">{formatValue(group.name)}</td>
                        <td>{formatValue(group.description)}</td>
                        <td>{group.products?.map(p => p.name).join(', ') || group.customers?.map(c => c.companyName).join(', ') || 'N/A'}</td>
                        <td className="action-buttons">
                          <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(group, null, 2))} colorClass="view" />
                          <ActionIconButton icon={<Edit size={16} />} label="Edit Group" onClick={() => alert('Edit group: ' + group.name)} colorClass="edit" />
                          <ActionIconButton icon={<Trash2 size={16} />} label="Delete Group" onClick={() => { if (window.confirm('Delete this group?')) setGroups(groups.filter(g => g.id !== group.id)); }} colorClass="delete" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {groups.length > PAGE_SIZE && (
                <div className="pagination-container">
                  <div className="pagination-summary">Showing {Math.min(groupPage * PAGE_SIZE, filteredGroups.length)} of {filteredGroups.length} entries</div>
                  <div className="pagination-buttons">
                    <button className="pagination-button" onClick={() => setGroupPage(p => Math.max(1, p - 1))} disabled={groupPage === 1}>Prev</button>
                    <span className="pagination-button active">{groupPage}</span>
                    <button className="pagination-button" onClick={() => setGroupPage(p => p + 1)} disabled={groupPage * PAGE_SIZE >= filteredGroups.length}>Next</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
  
        {/* Stock Sections */}
        <div className="stock-sections">
          <h3 className="subsection-title">Stock Sections</h3>
          <div className="stock-grid-cards">
            {stockSections.map(section => (
              <div key={section.key} className="stock-card-link" onClick={() => setSelectedStockType(section.key)}>
                {section.icon} {section.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderCreateSchedule = () => {
    const categoryError = getScheduleError('category');
    const customerError = getScheduleError('customer');
    const productIdError = getScheduleError('productId');
    const quantityError = getScheduleError('quantity');
    const validTillError = getScheduleError('validTill');
  
    const filtered = filteredData(schedules, ['category', 'customer', 'productName']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, schedulePage);
  
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Create Schedule</h2>
        </div>
  
        {/* FORM - Always visible (removed button condition) */}
        <div className="form-container" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
          <div className="form-grid">
            <div className="form-group">
              <label>Category <span className="required-star">*</span></label>
              <select value={scheduleForm.category} onChange={(e) => setScheduleForm({...scheduleForm, category: e.target.value})} className={categoryError ? 'error' : ''}>
                <option value="">Select Category</option>
                {categories.map(c => <option key={c.id}>{c.name}</option>)}
              </select>
              {categoryError && <span className="error-text">{categoryError}</span>}
            </div>
            <div className="form-group">
              <label>Sub Category <span className="optional-text">(Optional)</span></label>
              <input type="text" value={scheduleForm.subCategory} onChange={(e) => setScheduleForm({...scheduleForm, subCategory: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Group <span className="optional-text">(Optional)</span></label>
              <select value={scheduleForm.group} onChange={(e) => setScheduleForm({...scheduleForm, group: e.target.value})}>
                <option value="">Select Group</option>
                {groups.map(g => <option key={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Bought from customer <span className="required-star">*</span></label>
              <select value={scheduleForm.customer} onChange={(e) => setScheduleForm({...scheduleForm, customer: e.target.value})} className={customerError ? 'error' : ''}>
                <option value="">Select Customer</option>
                {customers.map(c => <option key={c.id}>{c.companyName}</option>)}
              </select>
              {customerError && <span className="error-text">{customerError}</span>}
            </div>
            <div className="form-group">
              <label>Product Name <span className="required-star">*</span></label>
              <select value={scheduleForm.productId} onChange={(e) => setScheduleForm({...scheduleForm, productId: e.target.value})} className={productIdError ? 'error' : ''}>
                <option value="">Select Product</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {productIdError && <span className="error-text">{productIdError}</span>}
            </div>
            <div className="form-group">
              <label>No of Quantity <span className="required-star">*</span></label>
              <input type="number" value={scheduleForm.quantity} onChange={(e) => setScheduleForm({...scheduleForm, quantity: e.target.value})} className={quantityError ? 'error' : ''} />
              {quantityError && <span className="error-text">{quantityError}</span>}
            </div>
            <div className="form-group">
              <label>Frequency <span className="required-star">*</span></label>
              <select value={scheduleForm.frequency} onChange={(e) => setScheduleForm({...scheduleForm, frequency: e.target.value})}>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
              </select>
            </div>
            <div className="form-group">
              <label>Valid Till date <span className="required-star">*</span></label>
              <input type="date" value={scheduleForm.validTill} onChange={(e) => setScheduleForm({...scheduleForm, validTill: e.target.value})} className={validTillError ? 'error' : ''} />
              {validTillError && <span className="error-text">{validTillError}</span>}
            </div>
          </div>
          <div className="form-actions">
            <button className="btn-cancel" onClick={() => {
              setScheduleForm({ category: '', subCategory: '', group: '', customer: '', productId: '', quantity: '', frequency: 'Weekly', validTill: '' });
              setScheduleSubmitAttempted(false);
            }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreateSchedule}>Create Schedule</button>
          </div>
        </div>
  
        {/* Stock Summary Section */}
        <div className="stock-summary-section">
          <h4>Stock Summary</h4>
          <div className="stock-summary-grid">
            <div className="stock-card finished">
              <h5>Finished Goods</h5>
              {stockData.finishedGoods.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
            </div>
            <div className="stock-card pending">
              <h5>Production Pending</h5>
              {stockData.productionStock.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
            </div>
            <div className="stock-card outsource">
              <h5>Outsource Pending</h5>
              {stockData.outsourcePending.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
            </div>
            <div className="stock-card raw">
              <h5>Raw Material</h5>
              {stockData.rmStock.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
            </div>
          </div>
        </div>
  
        {/* Existing Schedules List */}
        {schedules.length > 0 && (
          <div className="list-section">
            <h3><Calendar size={18} /> Existing Schedules ({schedules.length})</h3>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr><th>Product</th><th>Category</th><th>Customer</th><th>Quantity</th><th>Frequency</th><th>Valid Till</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {pageData.map(schedule => (
                    <tr key={schedule.id}>
                      <td className="highlight">{formatValue(schedule.productName)}</td>
                      <td>{formatValue(schedule.category)}</td>
                      <td>{formatValue(schedule.customer)}</td>
                      <td>{formatValue(schedule.quantity)}</td>
                      <td>{formatValue(schedule.frequency)}</td>
                      <td>{formatValue(schedule.validTill)}</td>
                      <td className="action-buttons">
                        <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(schedule, null, 2))} colorClass="view" />
                        <ActionIconButton icon={<Edit size={16} />} label="Edit Schedule" onClick={() => alert('Edit schedule')} colorClass="edit" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {schedules.length > PAGE_SIZE && (
              <Pagination currentPage={schedulePage} totalPages={totalPages} onPageChange={setSchedulePage} pageSize={PAGE_SIZE} totalItems={filtered.length} />
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCreatePO = () => {
    const customerIdError = getPOError('customerId');
    const supplierIdError = getPOError('supplierId');
    const deliveryDateError = getPOError('deliveryDate');
    const selectedProductsError = getPOError('selectedProducts');
  
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Create Purchase Order</h2>
        </div>
  
        {/* FORM - Always visible */}
        <div className="form-container" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
          <div className="form-grid">
            <div className="form-group">
              <label>PO Number</label>
              <input type="text" value={poForm.poNumber} disabled className="auto-field" />
              <small>Auto-generated</small>
            </div>
            <div className="form-group">
              <label>Order Date</label>
              <input type="date" value={poForm.orderDate} disabled className="auto-field" />
            </div>
            <div className="form-group">
              <label>Select Customer <span className="required-star">*</span></label>
              <select value={poForm.customerId} onChange={(e) => setPoForm({...poForm, customerId: e.target.value})} className={customerIdError ? 'error' : ''}>
                <option value="">Select Customer</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
              </select>
              {customerIdError && <span className="error-text">{customerIdError}</span>}
            </div>
            <div className="form-group">
              <label>Select Schedule <span className="optional-text">(Optional)</span></label>
              <select value={poForm.scheduleId} onChange={(e) => setPoForm({...poForm, scheduleId: e.target.value})}>
                <option value="">Select Schedule</option>
                {schedules.map(s => <option key={s.id} value={s.id}>{s.productName} - {s.customer}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Select Supplier <span className="required-star">*</span></label>
              <select value={poForm.supplierId} onChange={(e) => setPoForm({...poForm, supplierId: e.target.value})} className={supplierIdError ? 'error' : ''}>
                <option value="">Select Supplier</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.companyName}</option>)}
              </select>
              {supplierIdError && <span className="error-text">{supplierIdError}</span>}
            </div>
            <div className="form-group">
              <label>Delivery Date <span className="required-star">*</span></label>
              <input type="date" value={poForm.deliveryDate} onChange={(e) => setPoForm({...poForm, deliveryDate: e.target.value})} className={deliveryDateError ? 'error' : ''} />
              {deliveryDateError && <span className="error-text">{deliveryDateError}</span>}
            </div>
          </div>
  
          {/* Stock Details Section */}
          <div className="stock-summary-section">
            <h4>Store Details</h4>
            <div className="stock-summary-grid">
              <div className="stock-card finished">
                <h5>Finished Goods</h5>
                {stockData.finishedGoods.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
              </div>
              <div className="stock-card pending">
                <h5>Production Pending</h5>
                {stockData.productionStock.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
              </div>
              <div className="stock-card outsource">
                <h5>Outsource Pending</h5>
                {stockData.outsourcePending.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
              </div>
              <div className="stock-card raw">
                <h5>Raw Material</h5>
                {stockData.rmStock.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
              </div>
            </div>
          </div>
  
          <div className="select-products-section">
            <h4>Select Products <span className="required-star">*</span></h4>
            <select onChange={(e) => handleAddProductToPO(parseInt(e.target.value))} value="">
              <option value="">-- Add Product --</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name} - ₹{p.cost}</option>)}
            </select>
            {selectedProductsError && <span className="error-text">{selectedProductsError}</span>}
  
            {poForm.selectedProducts.length > 0 && (
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr><th>Product</th><th>Quantity</th><th>Unit Price</th><th>Total</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {poForm.selectedProducts.map(product => (
                      <tr key={product.id}>
                        <td>{product.name}</td>
                        <td><input type="number" value={product.quantity} onChange={(e) => handleUpdateProductQuantity(product.id, e.target.value, 'po')} style={{ width: '70px' }} /></td>
                        <td>₹{product.cost}</td>
                        <td>₹{(product.cost * product.quantity).toLocaleString()}</td>
                        <td><button className="action-btn delete" onClick={() => handleRemoveProduct('po', product.id)}><Trash2 size={16} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td colSpan="3"><strong>Total Amount</strong></td><td><strong>₹{poForm.totalAmount.toLocaleString()}</strong></td><td></td></tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
  
          <div className="form-actions">
            <button className="btn-cancel" onClick={() => {
              setPoForm({ poNumber: 'PO-' + Date.now(), customerId: '', scheduleId: '', supplierId: '', orderDate: new Date().toISOString().split('T')[0], deliveryDate: '', selectedProducts: [], totalAmount: 0, status: 'Pending' });
              setPoSubmitAttempted(false);
            }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreatePO}>Create PO</button>
          </div>
        </div>
  
        {/* Recent Purchase Orders */}
        {purchaseOrders.length > 0 && (
          <div className="list-section">
            <h3>Recent Purchase Orders</h3>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr><th>PO Number</th><th>Customer</th><th>Supplier</th><th>Amount</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {purchaseOrders.slice(0, 3).map(po => (
                    <tr key={po.id}>
                      <td className="highlight">{po.poNumber}</td>
                      <td>{po.customer?.companyName}</td>
                      <td>{po.supplier?.companyName}</td>
                      <td className="amount">₹{po.totalAmount?.toLocaleString()}</td>
                      <td><span className={`status-badge ${po.status.toLowerCase()}`}>{po.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderListPO = () => {
    const filtered = filteredData(purchaseOrders, ['poNumber', 'customer.companyName', 'supplier.companyName']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, poPage);

    if (selectedPOFilter === 'pending') {
      const pendingPOs = purchaseOrders.filter(po => po.status === 'Pending');
      return (
        <div>
          <div className="back-header">
            <button className="back-btn" onClick={() => setSelectedPOFilter(null)} title="Back"><ArrowLeft size={18} /> Back</button>
            <h2 className="section-title">List of Unapproved PO</h2>
          </div>
          <div className="filter-bar">
            <button className="action-btn view" title="Sort by PO ID"><Filter size={16} /> Sort by Po.id</button>
            <button className="action-btn view" title="Filter All Data"><Filter size={16} /> Filter By All Data</button>
          </div>
          {pendingPOs.length === 0 ? (
            <div className="empty-state"><Clock size={48} color="#CBD5E1" /><p>No pending purchase orders</p></div>
          ) : (
            <div className="po-cards-grid">
              {pendingPOs.map(po => (
                <div key={po.id} className="po-card">
                  <div className="po-card-header">
                    <h3>{po.poNumber}</h3>
                    <span className={`status-badge ${po.status.toLowerCase()}`}>{po.status}</span>
                  </div>
                  <div className="po-card-details">
                    <div><label>Customer</label><span>{po.customer?.companyName || 'N/A'}</span></div>
                    <div><label>Supplier</label><span>{po.supplier?.companyName || 'N/A'}</span></div>
                    <div><label>Order Date</label><span>{po.orderDate}</span></div>
                    <div><label>Delivery Date</label><span>{po.deliveryDate}</span></div>
                    <div><label>Total Amount</label><span className="amount">₹{po.totalAmount?.toLocaleString()}</span></div>
                    <div><label>Products</label><span>{po.selectedProducts?.map(p => `${p.name}(${p.quantity})`).join(', ') || 'N/A'}</span></div>
                  </div>
                  <div className="po-card-actions">
                    <RegularButton className="btn-approve" title="Approve Order" onClick={() => handleApprovePO(po.id)}><CheckCircle size={16} /> Approve</RegularButton>
                    <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(po, null, 2))} colorClass="view" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">List Purchase Order</h2>
          <button className="filter-btn" onClick={() => setSelectedPOFilter('pending')}><Clock size={16} /> View Pending</button>
        </div>
        <div className="filter-bar">
          <Search size={18} />
          <input type="text" placeholder="Search by PO Number, Customer, Supplier..." className="filter-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        {purchaseOrders.length === 0 ? (
          <div className="empty-state"><List size={48} color="#CBD5E1" /><p>No purchase orders found</p></div>
        ) : (
          <>
            <div className="po-cards-grid">
              {pageData.map(po => (
                <div key={po.id} className="po-card">
                  <div className="po-card-header">
                    <h3>{po.poNumber}</h3>
                    <span className={`status-badge ${po.status.toLowerCase()}`}>{po.status}</span>
                  </div>
                  <div className="po-card-details">
                    <div><label>Customer</label><span>{po.customer?.companyName || 'N/A'}</span></div>
                    <div><label>Supplier</label><span>{po.supplier?.companyName || 'N/A'}</span></div>
                    <div><label>Order Date</label><span>{po.orderDate}</span></div>
                    <div><label>Delivery Date</label><span>{po.deliveryDate}</span></div>
                    <div><label>Total Amount</label><span className="amount">₹{po.totalAmount?.toLocaleString()}</span></div>
                    <div><label>Products</label><span>{po.selectedProducts?.map(p => `${p.name}(${p.quantity})`).join(', ') || 'N/A'}</span></div>
                  </div>
                  <div className="po-card-actions">
                    <button className="btn-approve" title="Approve Order" onClick={() => handleApprovePO(po.id)}><CheckCircle size={16} /> Approve</button>
                    <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(po, null, 2))} colorClass="view" />
                    <ActionIconButton icon={<Edit size={16} />} label="Edit Order" onClick={() => alert('Edit PO: ' + po.poNumber)} colorClass="edit" />
                  </div>
                </div>
              ))}
            </div>
            {purchaseOrders.length > PAGE_SIZE && (
              <div className="pagination-container">
                <div className="pagination-summary">Showing {Math.min(poPage * PAGE_SIZE, filtered.length)} of {filtered.length} entries</div>
                <div className="pagination-buttons">
                  <button className="pagination-button" onClick={() => setPoPage(p => Math.max(1, p - 1))} disabled={poPage === 1}>Prev</button>
                  <span className="pagination-button active">{poPage}</span>
                  <button className="pagination-button" onClick={() => setPoPage(p => p + 1)} disabled={poPage * PAGE_SIZE >= filtered.length}>Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderCreateSaleOrder = () => {
    const customerIdError = getSOError('customerId');
    const selectedProductsError = getSOError('selectedProducts');
  
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Create Sale Order</h2>
        </div>
  
        {/* FORM - Always visible */}
        <div className="form-container" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
          <div className="form-grid">
            <div className="form-group">
              <label>SO Number</label>
              <input type="text" value={soForm.soNumber} disabled className="auto-field" />
              <small>Auto-generated</small>
            </div>
            <div className="form-group">
              <label>Order Date</label>
              <input type="date" value={soForm.orderDate} disabled className="auto-field" />
            </div>
            <div className="form-group">
              <label>Select Customer <span className="required-star">*</span></label>
              <select value={soForm.customerId} onChange={(e) => setSoForm({...soForm, customerId: e.target.value})} className={customerIdError ? 'error' : ''}>
                <option value="">Select Customer</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
              </select>
              {customerIdError && <span className="error-text">{customerIdError}</span>}
            </div>
            <div className="form-group">
              <label>Select Schedule <span className="optional-text">(Optional)</span></label>
              <select value={soForm.scheduleId} onChange={(e) => setSoForm({...soForm, scheduleId: e.target.value})}>
                <option value="">Select Schedule</option>
                {schedules.map(s => <option key={s.id} value={s.id}>{s.productName} - {s.customer}</option>)}
              </select>
            </div>
          </div>
  
          {/* Stock Details Section */}
          <div className="stock-summary-section">
            <h4>Store Details</h4>
            <div className="stock-summary-grid">
              <div className="stock-card finished">
                <h5>Finished Goods</h5>
                {stockData.finishedGoods.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
              </div>
            </div>
          </div>
  
          <div className="select-products-section">
            <h4>Select FG <span className="required-star">*</span></h4>
            <select onChange={(e) => handleAddProductToSO(parseInt(e.target.value))} value="">
              <option value="">-- Add Product --</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name} - Stock: {p.stock}</option>)}
            </select>
            {selectedProductsError && <span className="error-text">{selectedProductsError}</span>}
  
            {soForm.selectedProducts.length > 0 && (
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr><th>Product</th><th>Quantity</th><th>Unit Price</th><th>Total</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {soForm.selectedProducts.map(product => (
                      <tr key={product.id}>
                        <td>{product.name}</td>
                        <td><input type="number" value={product.quantity} onChange={(e) => handleUpdateProductQuantity(product.id, e.target.value, 'so')} style={{ width: '70px' }} /></td>
                        <td>₹{product.cost}</td>
                        <td>₹{(product.cost * product.quantity).toLocaleString()}</td>
                        <td><button className="action-btn delete" onClick={() => handleRemoveProduct('so', product.id)}><Trash2 size={16} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td colSpan="3"><strong>Total Amount</strong></td><td><strong>₹{soForm.totalAmount.toLocaleString()}</strong></td></tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
  
          <div className="form-actions">
            <button className="btn-cancel" onClick={() => {
              setSoForm({ soNumber: 'SO-' + Date.now(), customerId: '', scheduleId: '', orderDate: new Date().toISOString().split('T')[0], selectedProducts: [], totalAmount: 0, status: 'Pending' });
              setSoSubmitAttempted(false);
            }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreateSO}>Create Sale Order</button>
          </div>
        </div>
  
        {/* Recent Sale Orders */}
        {saleOrders.length > 0 && (
          <div className="list-section">
            <h3>Recent Sale Orders</h3>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr><th>SO Number</th><th>Customer</th><th>Amount</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {saleOrders.slice(0, 3).map(so => (
                    <tr key={so.id}>
                      <td className="highlight">{so.soNumber}</td>
                      <td>{so.customer?.companyName}</td>
                      <td className="amount">₹{so.totalAmount?.toLocaleString()}</td>
                      <td><span className={`status-badge ${so.status.toLowerCase()}`}>{so.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderListSaleOrder = () => {
    const filtered = filteredData(saleOrders, ['soNumber', 'customer.companyName']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, soPage);

    if (selectedSOFilter === 'pending') {
      const pendingSOs = saleOrders.filter(so => so.status === 'Pending');
      return (
        <div>
          <div className="back-header">
            <button className="back-btn" onClick={() => setSelectedSOFilter(null)} title="Back"><ArrowLeft size={18} /> Back</button>
            <h2 className="section-title">List of Pending Sale Order / Schedule</h2>
          </div>
          <div className="filter-bar">
            <button className="action-btn view" title="Sort by SO ID"><Filter size={16} /> Sort by So.id</button>
            <button className="action-btn view" title="Filter All Data"><Filter size={16} /> Filter By All Data</button>
          </div>
          {pendingSOs.length === 0 ? (
            <div className="empty-state"><Clock size={48} color="#CBD5E1" /><p>No pending sale orders</p></div>
          ) : (
            <div className="po-cards-grid">
              {pendingSOs.map(so => (
                <div key={so.id} className="po-card">
                  <div className="po-card-header">
                    <h3>{so.soNumber}</h3>
                    <span className={`status-badge ${so.status.toLowerCase()}`}>{so.status}</span>
                  </div>
                  <div className="po-card-details">
                    <div><label>Customer</label><span>{so.customer?.companyName || 'N/A'}</span></div>
                    <div><label>Order Date</label><span>{so.orderDate}</span></div>
                    <div><label>Total Amount</label><span className="amount">₹{so.totalAmount?.toLocaleString()}</span></div>
                    <div><label>Products</label><span>{so.selectedProducts?.map(p => `${p.name}(${p.quantity})`).join(', ') || 'N/A'}</span></div>
                  </div>
                  <div className="po-card-actions">
                    <button className="btn-approve" title="Approve Order" onClick={() => handleApproveSO(so.id)}><CheckCircle size={16} /> Approve</button>
                    <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(so, null, 2))} colorClass="view" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">List Sale Order</h2>
          <button className="filter-btn" onClick={() => setSelectedSOFilter('pending')}><Clock size={16} /> View Pending</button>
        </div>
        <div className="filter-bar">
          <Search size={18} />
          <input type="text" placeholder="Search by SO Number, Customer..." className="filter-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        {saleOrders.length === 0 ? (
          <div className="empty-state"><ShoppingCart size={48} color="#CBD5E1" /><p>No sale orders found</p></div>
        ) : (
          <>
            <div className="po-cards-grid">
              {pageData.map(so => (
                <div key={so.id} className="po-card">
                  <div className="po-card-header">
                    <h3>{so.soNumber}</h3>
                    <span className={`status-badge ${so.status.toLowerCase()}`}>{so.status}</span>
                  </div>
                  <div className="po-card-details">
                    <div><label>Customer</label><span>{so.customer?.companyName || 'N/A'}</span></div>
                    <div><label>Order Date</label><span>{so.orderDate}</span></div>
                    <div><label>Total Amount</label><span className="amount">₹{so.totalAmount?.toLocaleString()}</span></div>
                    <div><label>Products</label><span>{so.selectedProducts?.map(p => `${p.name}(${p.quantity})`).join(', ') || 'N/A'}</span></div>
                  </div>
                  <div className="po-card-actions">
                    <button className="btn-approve" title="Approve Order" onClick={() => handleApproveSO(so.id)}><CheckCircle size={16} /> Approve</button>
                    <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(so, null, 2))} colorClass="view" />
                    <ActionIconButton icon={<Edit size={16} />} label="Edit Order" onClick={() => alert('Edit SO: ' + so.soNumber)} colorClass="edit" />
                  </div>
                </div>
              ))}
            </div>
            {saleOrders.length > PAGE_SIZE && (
              <div className="pagination-container">
                <div className="pagination-summary">Showing {Math.min(soPage * PAGE_SIZE, filtered.length)} of {filtered.length} entries</div>
                <div className="pagination-buttons">
                  <button className="pagination-button" onClick={() => setSoPage(p => Math.max(1, p - 1))} disabled={soPage === 1}>Prev</button>
                  <span className="pagination-button active">{soPage}</span>
                  <button className="pagination-button" onClick={() => setSoPage(p => p + 1)} disabled={soPage * PAGE_SIZE >= filtered.length}>Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderDispatch = () => {
    const customerIdError = getDispatchError('customerId');
    const vehicleNameError = getDispatchError('vehicleName');
    const vehicleNumberError = getDispatchError('vehicleNumber');
    const driverNameError = getDispatchError('driverName');
  
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Dispatch</h2>
        </div>
  
        {/* FORM - Always visible (removed button condition) */}
        <div className="form-container" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
          <div className="form-grid">
            <div className="form-group">
              <label>Select Customer <span className="required-star">*</span></label>
              <select value={dispatchForm.customerId} onChange={(e) => setDispatchForm({...dispatchForm, customerId: e.target.value})} className={customerIdError ? 'error' : ''}>
                <option value="">Select Customer</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
              </select>
              {customerIdError && <span className="error-text">{customerIdError}</span>}
            </div>
            <div className="form-group">
              <label>Select Schedule <span className="optional-text">(Optional)</span></label>
              <select value={dispatchForm.scheduleId} onChange={(e) => setDispatchForm({...dispatchForm, scheduleId: e.target.value})}>
                <option value="">Select Schedule</option>
                {schedules.map(s => <option key={s.id} value={s.id}>{s.productName} - {s.customer}</option>)}
              </select>
            </div>
          </div>
  
          <div className="stock-summary-section">
            <h4>Scheduled List</h4>
            <div className="stock-summary-grid">
              {schedules.filter(s => s.customer === customers.find(c => c.id == dispatchForm.customerId)?.companyName).map(s => (
                <div key={s.id} className="stock-card pending">
                  <p>{s.productName}: {s.quantity}</p>
                </div>
              ))}
            </div>
          </div>
  
          <div className="stock-summary-section">
            <h4>Store Details</h4>
            <div className="stock-summary-grid">
              <div className="stock-card finished">
                <h5>Finished Goods</h5>
                {stockData.finishedGoods.map((item, idx) => <p key={idx}>{item.name}: {item.quantity}</p>)}
              </div>
            </div>
          </div>
  
          <div className="select-products-section">
            <h4>Select FG</h4>
            <select onChange={(e) => handleAddProductToDispatch(parseInt(e.target.value))} value="">
              <option value="">-- Add Product --</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name} - Stock: {p.stock}</option>)}
            </select>
            {dispatchForm.selectedProducts.length > 0 && (
              <div className="data-table-container">
                <table className="data-table">
                  <thead><tr><th>Product</th><th>Quantity</th><th>Action</th></tr></thead>
                  <tbody>
                    {dispatchForm.selectedProducts.map(product => (
                      <tr key={product.id}>
                        <td>{product.name}</td>
                        <td><input type="number" value={product.quantity} onChange={(e) => handleUpdateProductQuantity(product.id, e.target.value, 'dispatch')} style={{ width: '80px' }} /></td>
                        <td><button className="action-btn delete" onClick={() => handleRemoveProduct('dispatch', product.id)}><Trash2 size={16} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
  
          <div className="form-grid">
            <div className="form-group">
              <label>Vehicle Name <span className="required-star">*</span></label>
              <input type="text" value={dispatchForm.vehicleName} onChange={(e) => setDispatchForm({...dispatchForm, vehicleName: e.target.value})} className={vehicleNameError ? 'error' : ''} />
              {vehicleNameError && <span className="error-text">{vehicleNameError}</span>}
            </div>
            <div className="form-group">
              <label>Vehicle Number <span className="required-star">*</span></label>
              <input type="text" value={dispatchForm.vehicleNumber} onChange={(e) => setDispatchForm({...dispatchForm, vehicleNumber: e.target.value})} className={vehicleNumberError ? 'error' : ''} />
              {vehicleNumberError && <span className="error-text">{vehicleNumberError}</span>}
            </div>
            <div className="form-group">
              <label>Driver Name <span className="required-star">*</span></label>
              <input type="text" value={dispatchForm.driverName} onChange={(e) => setDispatchForm({...dispatchForm, driverName: e.target.value})} className={driverNameError ? 'error' : ''} />
              {driverNameError && <span className="error-text">{driverNameError}</span>}
            </div>
            <div className="form-group">
              <label>License & Validity <span className="optional-text">(Optional)</span></label>
              <input type="date" value={dispatchForm.licenseValidity} onChange={(e) => setDispatchForm({...dispatchForm, licenseValidity: e.target.value})} />
            </div>
          </div>
  
          <div className="form-actions">
            <button className="btn-cancel" onClick={() => {
              setDispatchForm({ customerId: '', scheduleId: '', selectedProducts: [], selectedScrap: [], selectedReturn: [], selectedNC: [], vehicleName: '', vehicleNumber: '', driverName: '', licenseValidity: '' });
              setDispatchSubmitAttempted(false);
            }}>Cancel</button>
            <button className="btn-submit" onClick={handleDispatch}>Generate</button>
          </div>
        </div>
      </div>
    );
  };

  const renderListGRN = () => {
    const filtered = filteredData(grns, ['grnNumber', 'poNumber', 'supplier', 'product']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, grnPage);

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Goods Received Note (GRN)</h2>
        </div>
        <div className="filter-bar">
          <Search size={18} />
          <input type="text" placeholder="Search GRN by Number, PO, Supplier..." className="filter-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        {grns.length === 0 ? (
          <div className="empty-state"><FileCheck size={48} color="#CBD5E1" /><p>No GRN records found</p></div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>GRN Number</th><th>PO Number</th><th>Supplier</th><th>Product</th><th>Quantity</th><th>Supplier Invoice No</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map(grn => (
                    <tr key={grn.id}>
                      <td className="highlight">{formatValue(grn.grnNumber)}</td>
                      <td>{formatValue(grn.poNumber)}</td>
                      <td>{formatValue(grn.supplier)}</td>
                      <td>{formatValue(grn.product)}</td>
                      <td>{formatValue(grn.quantity)}</td>
                      <td>{formatValue(grn.supplierInvoiceNo)}</td>
                      <td><span className={`status-badge ${grn.status?.toLowerCase() === 'approved' ? 'approved' : 'pending'}`}>{formatValue(grn.status)}</span></td>
                      <td className="action-buttons">
                        <button className="btn-approve" title="Submit GRN"><CheckCircle size={16} /> Submit</button>
                        <ActionIconButton icon={<Mail size={16} />} label="Mail to Supplier" onClick={() => alert('Mail to supplier for GRN: ' + grn.grnNumber)} colorClass="view" />
                        <ActionIconButton icon={<Edit size={16} />} label="Edit GRN" onClick={() => alert('Edit GRN: ' + grn.grnNumber)} colorClass="edit" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {grns.length > PAGE_SIZE && (
              <div className="pagination-container">
                <div className="pagination-summary">Showing {Math.min(grnPage * PAGE_SIZE, filtered.length)} of {filtered.length} entries</div>
                <div className="pagination-buttons">
                  <button className="pagination-button" onClick={() => setGrnPage(p => Math.max(1, p - 1))} disabled={grnPage === 1}>Prev</button>
                  <span className="pagination-button active">{grnPage}</span>
                  <button className="pagination-button" onClick={() => setGrnPage(p => p + 1)} disabled={grnPage * PAGE_SIZE >= filtered.length}>Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderListInvoices = () => {
    const filtered = filteredData(invoices, ['invoiceNumber', 'customer']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, invoicePage);

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">List Invoices</h2>
        </div>
        <div className="filter-bar">
          <Search size={18} />
          <input type="text" placeholder="Search by Invoice Number, Customer..." className="filter-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        {invoices.length === 0 ? (
          <div className="empty-state"><FileText size={48} color="#CBD5E1" /><p>No invoices found</p></div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice Number</th><th>Customer</th><th>Date</th><th>Amount</th><th>CGST</th><th>SGST</th><th>IGST</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map(invoice => (
                    <tr key={invoice.id}>
                      <td className="highlight">{formatValue(invoice.invoiceNumber)}</td>
                      <td>{formatValue(invoice.customer)}</td>
                      <td>{formatValue(invoice.date)}</td>
                      <td className="amount">₹{formatValue(invoice.amount)}</td>
                      <td>{formatValue(invoice.cgst)}%</td>
                      <td>{formatValue(invoice.sgst)}%</td>
                      <td>{formatValue(invoice.igst)}%</td>
                      <td><span className={`status-badge ${invoice.status === 'Paid' ? 'approved' : 'pending'}`}>{formatValue(invoice.status)}</span></td>
                      <td className="action-buttons">
                        <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(invoice, null, 2))} colorClass="view" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {invoices.length > PAGE_SIZE && (
              <Pagination currentPage={invoicePage} totalPages={totalPages} onPageChange={setInvoicePage} pageSize={PAGE_SIZE} totalItems={filtered.length} />
            )}
          </>
        )}
      </div>
    );
  };

  const renderSupplierPayment = () => {
    const grnIdError = getPaymentError('grnId');
    const amountError = getPaymentError('amount');
    const utrNumberError = getPaymentError('utrNumber');
  
    const filtered = filteredData(grns, ['grnNumber', 'supplier']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, grnPage);
  
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Supplier Payment</h2>
        </div>
  
        {/* FORM - Always visible */}
        <div className="form-container" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
          <div className="form-grid">
            <div className="form-group">
              <label>Payment ID</label>
              <input type="text" value={paymentForm.paymentId} disabled className="auto-field" />
              <small>Auto-generated</small>
            </div>
            <div className="form-group">
              <label>Payment Date</label>
              <input type="date" value={paymentForm.paymentDate} onChange={(e) => setPaymentForm({...paymentForm, paymentDate: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Select GRN <span className="required-star">*</span></label>
              <select value={paymentForm.grnId} onChange={(e) => setPaymentForm({...paymentForm, grnId: e.target.value})} className={grnIdError ? 'error' : ''}>
                <option value="">Select GRN</option>
                {grns.map(g => <option key={g.id} value={g.grnNumber}>{g.grnNumber} - {g.supplier} - ₹{g.quantity * 1000}</option>)}
              </select>
              {grnIdError && <span className="error-text">{grnIdError}</span>}
            </div>
            <div className="form-group">
              <label>Amount <span className="required-star">*</span></label>
              <input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value)})} className={amountError ? 'error' : ''} placeholder="Enter amount" />
              {amountError && <span className="error-text">{amountError}</span>}
            </div>
            <div className="form-group">
              <label>Payment Mode <span className="required-star">*</span></label>
              <select value={paymentForm.paymentMode} onChange={(e) => setPaymentForm({...paymentForm, paymentMode: e.target.value})}>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
                <option value="Cash">Cash</option>
              </select>
            </div>
            <div className="form-group">
              <label>UTR Number <span className="required-star">*</span></label>
              <input type="text" value={paymentForm.utrNumber} onChange={(e) => setPaymentForm({...paymentForm, utrNumber: e.target.value})} className={utrNumberError ? 'error' : ''} placeholder="Enter UTR number" />
              {utrNumberError && <span className="error-text">{utrNumberError}</span>}
            </div>
            <div className="form-group">
              <label>Verified</label>
              <input type="checkbox" checked={paymentForm.verified} onChange={(e) => setPaymentForm({...paymentForm, verified: e.target.checked})} style={{ width: 'auto' }} />
            </div>
          </div>
          <div className="form-actions">
            <button className="btn-cancel" onClick={() => {
              setPaymentForm({ paymentId: 'PAY-' + Date.now(), grnId: '', amount: '', paymentDate: new Date().toISOString().split('T')[0], paymentMode: 'Bank Transfer', utrNumber: '', verified: false });
              setSupplierPaymentSubmitAttempted(false);
            }}>Cancel</button>
            <button className="btn-submit" onClick={handleSupplierPayment}>Submit Payment</button>
          </div>
        </div>
  
        {/* List GRN Section */}
        <div className="list-section">
          <h3><FileCheck size={18} /> List GRN</h3>
          <div className="filter-bar">
            <Search size={18} />
            <input type="text" placeholder="Search GRN..." className="filter-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr><th>GRN Number</th><th>Supplier</th><th>Product</th><th>Quantity</th><th>Supplier Invoice No</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {pageData.map(grn => (
                  <tr key={grn.id}>
                    <td className="highlight">{formatValue(grn.grnNumber)}</td>
                    <td>{formatValue(grn.supplier)}</td>
                    <td>{formatValue(grn.product)}</td>
                    <td>{formatValue(grn.quantity)}</td>
                    <td>{formatValue(grn.supplierInvoiceNo)}</td>
                    <td className="action-buttons">
                      <ActionIconButton icon={<Edit size={16} />} label="Edit GRN" onClick={() => alert('Edit GRN: ' + grn.grnNumber)} colorClass="edit" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {grns.length > PAGE_SIZE && (
            <Pagination currentPage={grnPage} totalPages={totalPages} onPageChange={setGrnPage} pageSize={PAGE_SIZE} totalItems={filtered.length} />
          )}
        </div>
      </div>
    );
  };

  const renderCustomerPayment = () => {
    const invoiceIdError = getCustomerPaymentError('invoiceId');
    const amountError = getCustomerPaymentError('amount');
    const utrNumberError = getCustomerPaymentError('utrNumber');
  
    const filtered = filteredData(invoices, ['invoiceNumber', 'customer']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, invoicePage);
  
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Debtor / Customer Invoice Payment</h2>
        </div>
  
        {/* FORM - Always visible */}
        <div className="form-container" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
          <div className="form-grid">
            <div className="form-group">
              <label>Payment ID</label>
              <input type="text" value={customerPaymentForm.paymentId} disabled className="auto-field" />
              <small>Auto-generated</small>
            </div>
            <div className="form-group">
              <label>Payment Date</label>
              <input type="date" value={customerPaymentForm.paymentDate} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, paymentDate: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Select Invoice <span className="required-star">*</span></label>
              <select value={customerPaymentForm.invoiceId} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, invoiceId: e.target.value})} className={invoiceIdError ? 'error' : ''}>
                <option value="">Select Invoice</option>
                {invoices.map(inv => <option key={inv.id} value={inv.id}>{inv.invoiceNumber} - {inv.customer} - ₹{inv.amount}</option>)}
              </select>
              {invoiceIdError && <span className="error-text">{invoiceIdError}</span>}
            </div>
            <div className="form-group">
              <label>Amount <span className="required-star">*</span></label>
              <input type="number" value={customerPaymentForm.amount} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, amount: parseFloat(e.target.value)})} className={amountError ? 'error' : ''} placeholder="Enter amount" />
              {amountError && <span className="error-text">{amountError}</span>}
            </div>
            <div className="form-group">
              <label>Payment Mode <span className="required-star">*</span></label>
              <select value={customerPaymentForm.paymentMode} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, paymentMode: e.target.value})}>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
                <option value="Cash">Cash</option>
              </select>
            </div>
            <div className="form-group">
              <label>UTR Number <span className="required-star">*</span></label>
              <input type="text" value={customerPaymentForm.utrNumber} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, utrNumber: e.target.value})} className={utrNumberError ? 'error' : ''} placeholder="Enter UTR number" />
              {utrNumberError && <span className="error-text">{utrNumberError}</span>}
            </div>
            <div className="form-group">
              <label>CGST (%) <span className="optional-text">(Optional)</span></label>
              <input type="number" value={customerPaymentForm.cgst} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, cgst: parseFloat(e.target.value)})} />
            </div>
            <div className="form-group">
              <label>SGST (%) <span className="optional-text">(Optional)</span></label>
              <input type="number" value={customerPaymentForm.sgst} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, sgst: parseFloat(e.target.value)})} />
            </div>
            <div className="form-group">
              <label>IGST (%) <span className="optional-text">(Optional)</span></label>
              <input type="number" value={customerPaymentForm.igst} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, igst: parseFloat(e.target.value)})} />
            </div>
            <div className="form-group">
              <label>Verified</label>
              <input type="checkbox" checked={customerPaymentForm.verified} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, verified: e.target.checked})} style={{ width: 'auto' }} />
            </div>
          </div>
          <div className="form-actions">
            <button className="btn-cancel" onClick={() => {
              setCustomerPaymentForm({ paymentId: 'PAY-' + Date.now(), invoiceId: '', amount: '', paymentDate: new Date().toISOString().split('T')[0], paymentMode: 'Bank Transfer', utrNumber: '', cgst: 0, sgst: 0, igst: 0, verified: false });
              setCustomerPaymentSubmitAttempted(false);
            }}>Cancel</button>
            <button className="btn-submit" onClick={handleCustomerPayment}>Submit Payment</button>
          </div>
        </div>
  
        {/* List Invoices Section */}
        <div className="list-section">
          <h3><FileText size={18} /> List of Invoice with respect to invoice number</h3>
          <div className="filter-bar">
            <Search size={18} />
            <input type="text" placeholder="Search invoices..." className="filter-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr><th>Invoice Number</th><th>Customer</th><th>Amount</th><th>CGST</th><th>SGST</th><th>IGST</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {pageData.map(invoice => (
                  <tr key={invoice.id}>
                    <td className="highlight">{formatValue(invoice.invoiceNumber)}</td>
                    <td>{formatValue(invoice.customer)}</td>
                    <td className="amount">₹{formatValue(invoice.amount)}</td>
                    <td>{formatValue(invoice.cgst)}%</td>
                    <td>{formatValue(invoice.sgst)}%</td>
                    <td>{formatValue(invoice.igst)}%</td>
                    <td><span className={`status-badge ${invoice.status === 'Paid' ? 'approved' : 'pending'}`}>{formatValue(invoice.status)}</span></td>
                    <td className="action-buttons">
                      <ActionIconButton icon={<Edit size={16} />} label="Edit Invoice" onClick={() => alert('Edit invoice: ' + invoice.invoiceNumber)} colorClass="edit" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {invoices.length > PAGE_SIZE && (
            <Pagination currentPage={invoicePage} totalPages={totalPages} onPageChange={setInvoicePage} pageSize={PAGE_SIZE} totalItems={filtered.length} />
          )}
        </div>
      </div>
    );
  };

  // Modal render functions
  const renderCreateCustomerModal = () => {
    const pocNameError = getCustomerError('pocName');
    const companyNameError = getCustomerError('companyName');
    const usernameError = getCustomerError('username');
    const passwordError = getCustomerError('password');
    const addressError = getCustomerError('address');

    return showCreateCustomer && (
      <div className="modal-overlay" onClick={() => { setShowCreateCustomer(false); setCustomerSubmitAttempted(false); }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Create Customer</h2>
            <button className="modal-close" onClick={() => { setShowCreateCustomer(false); setCustomerSubmitAttempted(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>POC Name <span className="required-star">*</span></label>
                <input type="text" value={customerForm.pocName} onChange={(e) => setCustomerForm({...customerForm, pocName: e.target.value})} className={pocNameError ? 'error' : ''} />
                {pocNameError && <span className="error-text">{pocNameError}</span>}
              </div>
              <div className="form-group">
                <label>Company Name <span className="required-star">*</span></label>
                <input type="text" value={customerForm.companyName} onChange={(e) => setCustomerForm({...customerForm, companyName: e.target.value})} className={companyNameError ? 'error' : ''} />
                {companyNameError && <span className="error-text">{companyNameError}</span>}
              </div>
              <div className="form-group">
                <label>Username</label>
                <input type="text" value={customerForm.username} disabled onChange={(e) => setCustomerForm({...customerForm, username: e.target.value})} className={usernameError ? 'error' : ''} />
                {usernameError && <span className="error-text">{usernameError}</span>}
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" value={customerForm.password} disabled onChange={(e) => setCustomerForm({...customerForm, password: e.target.value})} className={passwordError ? 'error' : ''} />
                {passwordError && <span className="error-text">{passwordError}</span>}
              </div>
              <div className="form-group full-width">
                <label>Address <span className="required-star">*</span></label>
                <textarea rows="3" value={customerForm.address} onChange={(e) => setCustomerForm({...customerForm, address: e.target.value})} className={addressError ? 'error' : ''} placeholder="Enter complete address" />
                {addressError && <span className="error-text">{addressError}</span>}
              </div>
              <div className="form-group">
                <label>PAN <span className="required-star">*</span></label>
                <input type="text" value={customerForm.pan} onChange={(e) => setCustomerForm({...customerForm, pan: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Bank Details <span className="required-star">*</span></label>
                <input type="text" value={customerForm.bankDetails} onChange={(e) => setCustomerForm({...customerForm, bankDetails: e.target.value})} />
              </div>
              <div className="form-group">
                <label>GST <span className="required-star">*</span></label>
                <input type="text" value={customerForm.gst} onChange={(e) => setCustomerForm({...customerForm, gst: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Phone <span className="required-star">*</span></label>
                <input type="tel" value={customerForm.phone} onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Vendor Code</label>
                <input type="text" value={customerForm.vendorCode} disabled className="auto-field" />
                <small>Auto-generated</small>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => { setShowCreateCustomer(false); setCustomerSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreateCustomer}>Submit</button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateSupplierModal = () => {
    const pocNameError = getSupplierError('pocName');
    const companyNameError = getSupplierError('companyName');
    const usernameError = getSupplierError('username');
    const passwordError = getSupplierError('password');
    const addressError = getSupplierError('address');

    return showCreateSupplier && (
      <div className="modal-overlay" onClick={() => { setShowCreateSupplier(false); setSupplierSubmitAttempted(false); }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Create Supplier</h2>
            <button className="modal-close" onClick={() => { setShowCreateSupplier(false); setSupplierSubmitAttempted(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>POC Name <span className="required-star">*</span></label>
                <input type="text" value={supplierForm.pocName} onChange={(e) => setSupplierForm({...supplierForm, pocName: e.target.value})} className={pocNameError ? 'error' : ''} />
                {pocNameError && <span className="error-text">{pocNameError}</span>}
              </div>
              <div className="form-group">
                <label>Company Name <span className="required-star">*</span></label>
                <input type="text" value={supplierForm.companyName} onChange={(e) => setSupplierForm({...supplierForm, companyName: e.target.value})} className={companyNameError ? 'error' : ''} />
                {companyNameError && <span className="error-text">{companyNameError}</span>}
              </div>
              <div className="form-group">
                <label>Username <span className="required-star">*</span></label>
                <input type="text" value={supplierForm.username} onChange={(e) => setSupplierForm({...supplierForm, username: e.target.value})} className={usernameError ? 'error' : ''} />
                {usernameError && <span className="error-text">{usernameError}</span>}
              </div>
              <div className="form-group">
                <label>Password <span className="required-star">*</span></label>
                <input type="password" value={supplierForm.password} onChange={(e) => setSupplierForm({...supplierForm, password: e.target.value})} className={passwordError ? 'error' : ''} />
                {passwordError && <span className="error-text">{passwordError}</span>}
              </div>
              <div className="form-group full-width">
                <label>Address <span className="required-star">*</span></label>
                <textarea rows="3" value={supplierForm.address} onChange={(e) => setSupplierForm({...supplierForm, address: e.target.value})} className={addressError ? 'error' : ''} placeholder="Enter complete address" />
                {addressError && <span className="error-text">{addressError}</span>}
              </div>
              <div className="form-group">
                <label>PAN <span className="optional-text">(Optional)</span></label>
                <input type="text" value={supplierForm.pan} onChange={(e) => setSupplierForm({...supplierForm, pan: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Bank Details <span className="optional-text">(Optional)</span></label>
                <input type="text" value={supplierForm.bankDetails} onChange={(e) => setSupplierForm({...supplierForm, bankDetails: e.target.value})} />
              </div>
              <div className="form-group">
                <label>GST <span className="optional-text">(Optional)</span></label>
                <input type="text" value={supplierForm.gst} onChange={(e) => setSupplierForm({...supplierForm, gst: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Phone <span className="optional-text">(Optional)</span></label>
                <input type="tel" value={supplierForm.phone} onChange={(e) => setSupplierForm({...supplierForm, phone: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Supplier Code</label>
                <input type="text" value={supplierForm.supplierCode} disabled className="auto-field" />
                <small>Auto-generated</small>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => { setShowCreateSupplier(false); setSupplierSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreateSupplier}>Submit</button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateCategoryModal = () => {
    const nameError = getCategoryError('name');
    return showCreateCategory && (
      <div className="modal-overlay" onClick={() => { setShowCreateCategory(false); setCategorySubmitAttempted(false); }}>
        <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Add Category</h2>
            <button className="modal-close" onClick={() => { setShowCreateCategory(false); setCategorySubmitAttempted(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label>Category Name <span className="required-star">*</span></label>
              <input type="text" value={categoryForm.name} onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})} className={nameError ? 'error' : ''} placeholder="Enter category name" />
              {nameError && <span className="error-text">{nameError}</span>}
            </div>
            <div className="form-group">
              <label>Description <span className="optional-text">(Optional)</span></label>
              <textarea rows="2" value={categoryForm.description} onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})} placeholder="Enter description" />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => { setShowCreateCategory(false); setCategorySubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreateCategory}>Add</button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateProductModal = () => {
    const nameError = getProductError('name');
    const codeError = getProductError('code');
    const categoryError = getProductError('category');
    const costError = getProductError('cost');

    return showCreateProduct && (
      <div className="modal-overlay" onClick={() => { setShowCreateProduct(false); setProductSubmitAttempted(false); }}>
        <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Add Product</h2>
            <button className="modal-close" onClick={() => { setShowCreateProduct(false); setProductSubmitAttempted(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Product Name <span className="required-star">*</span></label>
                <input type="text" value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} className={nameError ? 'error' : ''} />
                {nameError && <span className="error-text">{nameError}</span>}
              </div>
              <div className="form-group">
                <label>Product Code <span className="required-star">*</span></label>
                <input type="text" value={productForm.code} onChange={(e) => setProductForm({...productForm, code: e.target.value})} className={codeError ? 'error' : ''} />
                {codeError && <span className="error-text">{codeError}</span>}
              </div>
              
              <div className="form-group full-width">
                <label>Description <span className="optional-text">(Optional)</span></label>
                <textarea rows="2" value={productForm.description} onChange={(e) => setProductForm({...productForm, description: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Category <span className="required-star">*</span></label>
                <select value={productForm.category} onChange={(e) => setProductForm({...productForm, category: e.target.value})} className={categoryError ? 'error' : ''}>
                  <option value="">Select Category</option>
                  {categories.map(c => <option key={c.id}>{c.name}</option>)}
                </select>
                {categoryError && <span className="error-text">{categoryError}</span>}
              </div>
              <div className="form-group">
                <label>Dimension (sq mm) <span className="optional-text">(Optional)</span></label>
                <input type="text" value={productForm.dimension} onChange={(e) => setProductForm({...productForm, dimension: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Extra Dimension <span className="optional-text">(Optional)</span></label>
                <input type="text" value={productForm.extraDimension} onChange={(e) => setProductForm({...productForm, extraDimension: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Total Dimension</label>
                <input type="text" value={productForm.totalDimension} disabled className="auto-field" />
                <small>Auto-calculated</small>
              </div>
              <div className="form-group">
                <label>Cost <span className="required-star">*</span></label>
                <input type="number" value={productForm.cost} onChange={(e) => setProductForm({...productForm, cost: parseFloat(e.target.value)})} className={costError ? 'error' : ''} />
                {costError && <span className="error-text">{costError}</span>}
              </div>
              <div className="form-group">
                <label>Initial Stock <span className="optional-text">(Optional)</span></label>
                <input type="number" value={productForm.stock} onChange={(e) => setProductForm({...productForm, stock: parseInt(e.target.value)})} />
              </div>
              <div className="form-group">
                <label>Upload Image <span className="optional-text">(Optional)</span></label>
                <input type="file" accept="image/*" onChange={(e) => setProductForm({...productForm, image: e.target.files[0]})} />
              </div>
              <div className="form-group">
                <label>3D Model (gltf) <span className="optional-text">(Optional)</span></label>
                <input type="file" accept=".gltf,.glb" onChange={(e) => setProductForm({...productForm, model3d: e.target.files[0]})} />
              </div>
            </div>

            {/* Long Text Fields Section */}
            <div className="long-text-fields-section">
              <h4>Additional Information</h4>
              <div className="form-group full-width">
                <label>Product Description <span className="optional-text">(Optional)</span></label>
                <textarea rows="3" value={longTextFields.description} onChange={(e) => handleLongTextFieldChange('description', e.target.value)} placeholder="Enter detailed product description..." />
              </div>
              <div className="form-group full-width">
                <label>Terms & Conditions <span className="optional-text">(Optional)</span></label>
                <textarea rows="2" value={longTextFields.terms} onChange={(e) => handleLongTextFieldChange('terms', e.target.value)} placeholder="Enter terms and conditions..." />
              </div>
              <div className="form-group full-width">
                <label>Additional Notes <span className="optional-text">(Optional)</span></label>
                <textarea rows="2" value={longTextFields.notes} onChange={(e) => handleLongTextFieldChange('notes', e.target.value)} placeholder="Enter any additional notes..." />
              </div>
            </div>

            {/* Extra Fields Section */}
            <div className="extra-fields-section">
              <label>Extra Fields</label>
              {extraFields.map((field, index) => (
                <div key={index} className="extra-field-row">
                  <input
                    type="text"
                    className="extra-field-input"
                    placeholder="Field Name"
                    value={field.key}
                    onChange={(e) => handleExtraFieldChange(index, 'key', e.target.value)}
                  />
                  <input
                    type="text"
                    className="extra-field-input"
                    placeholder="Value"
                    value={field.value}
                    onChange={(e) => handleExtraFieldChange(index, 'value', e.target.value)}
                  />
                  <button className="remove-field-btn" onClick={() => handleRemoveExtraField(index)}>
                    <X size={16} />
                  </button>
                </div>
              ))}
              <button className="add-field-btn" onClick={handleAddExtraField}>
                <PlusCircle size={14} /> Add Extra Field
              </button>
            </div>

            <div className="child-product-section">
              <h4>Need to add child product and sub child product</h4>
              <button className="add-field-btn" onClick={() => setShowChildProduct(!showChildProduct)}>
                <PlusCircle size={14} /> Add Child Product
              </button>
              {showChildProduct && (
                <div className="child-product-form" style={{ marginTop: '12px', padding: '12px', background: '#F1F5F9', borderRadius: '8px' }}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Child Product Name</label>
                      <input type="text" placeholder="Enter child product name" />
                    </div>
                    <div className="form-group">
                      <label>Quantity</label>
                      <input type="number" placeholder="Quantity" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="raw-material-section" style={{ marginTop: '16px' }}>
              <h4>Select Raw Material for child product</h4>
              <button className="add-field-btn" onClick={() => setShowRawMaterialSelection(!showRawMaterialSelection)}>
                <PlusCircle size={14} /> Select Raw Material
              </button>
              {showRawMaterialSelection && (
                <div className="raw-material-form" style={{ marginTop: '12px' }}>
                  <div className="form-group">
                    <label>Raw Material Type</label>
                    <select>
                      <option value="">Select Raw Material</option>
                      {stockData.rmStock.map(rm => <option key={rm.id}>{rm.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Quantity</label>
                    <input type="number" placeholder="Quantity" />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => { setShowCreateProduct(false); setProductSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreateProduct}>Add Product</button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateGroupModal = () => {
    const nameError = getGroupError('name');
    return showCreateGroup && (
      <div className="modal-overlay" onClick={() => { setShowCreateGroup(false); setGroupSubmitAttempted(false); }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Create Group</h2>
            <button className="modal-close" onClick={() => { setShowCreateGroup(false); setGroupSubmitAttempted(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label>Group Name <span className="required-star">*</span></label>
              <input type="text" value={groupForm.name} onChange={(e) => setGroupForm({...groupForm, name: e.target.value})} className={nameError ? 'error' : ''} placeholder="Enter group name" />
              {nameError && <span className="error-text">{nameError}</span>}
            </div>
            <div className="form-group">
              <label>Description <span className="optional-text">(Optional)</span></label>
              <textarea rows="2" value={groupForm.description} onChange={(e) => setGroupForm({...groupForm, description: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Select Products <span className="optional-text">(Optional)</span></label>
              <select multiple size="5" value={groupForm.customers} onChange={(e) => setGroupForm({...groupForm, customers: Array.from(e.target.selectedOptions, o => parseInt(o.value))})}>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <small>Hold Ctrl/Cmd to select multiple products</small>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => { setShowCreateGroup(false); setGroupSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreateGroup}>Add</button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateScheduleModal = () => {
    const categoryError = getScheduleError('category');
    const customerError = getScheduleError('customer');
    const productIdError = getScheduleError('productId');
    const quantityError = getScheduleError('quantity');
    const validTillError = getScheduleError('validTill');

    return showCreateSchedule && (
      <div className="modal-overlay" onClick={() => { setShowCreateSchedule(false); setScheduleSubmitAttempted(false); }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Create Schedule</h2>
            <button className="modal-close" onClick={() => { setShowCreateSchedule(false); setScheduleSubmitAttempted(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Category <span className="required-star">*</span></label>
                <select value={scheduleForm.category} onChange={(e) => setScheduleForm({...scheduleForm, category: e.target.value})} className={categoryError ? 'error' : ''}>
                  <option value="">Select Category</option>
                  {categories.map(c => <option key={c.id}>{c.name}</option>)}
                </select>
                {categoryError && <span className="error-text">{categoryError}</span>}
              </div>
              <div className="form-group">
                <label>Sub Category <span className="optional-text">(Optional)</span></label>
                <input type="text" value={scheduleForm.subCategory} onChange={(e) => setScheduleForm({...scheduleForm, subCategory: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Group <span className="optional-text">(Optional)</span></label>
                <select value={scheduleForm.group} onChange={(e) => setScheduleForm({...scheduleForm, group: e.target.value})}>
                  <option value="">Select Group</option>
                  {groups.map(g => <option key={g.id}>{g.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Bought from customer <span className="required-star">*</span></label>
                <select value={scheduleForm.customer} onChange={(e) => setScheduleForm({...scheduleForm, customer: e.target.value})} className={customerError ? 'error' : ''}>
                  <option value="">Select Customer</option>
                  {customers.map(c => <option key={c.id}>{c.companyName}</option>)}
                </select>
                {customerError && <span className="error-text">{customerError}</span>}
              </div>
              <div className="form-group">
                <label>Product Name <span className="required-star">*</span></label>
                <select value={scheduleForm.productId} onChange={(e) => setScheduleForm({...scheduleForm, productId: e.target.value})} className={productIdError ? 'error' : ''}>
                  <option value="">Select Product</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                {productIdError && <span className="error-text">{productIdError}</span>}
              </div>
              <div className="form-group">
                <label>No of Quantity <span className="required-star">*</span></label>
                <input type="number" value={scheduleForm.quantity} onChange={(e) => setScheduleForm({...scheduleForm, quantity: e.target.value})} className={quantityError ? 'error' : ''} />
                {quantityError && <span className="error-text">{quantityError}</span>}
              </div>
              <div className="form-group">
                <label>Frequency <span className="required-star">*</span></label>
                <select value={scheduleForm.frequency} onChange={(e) => setScheduleForm({...scheduleForm, frequency: e.target.value})}>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                </select>
              </div>
              <div className="form-group">
                <label>Valid Till date <span className="required-star">*</span></label>
                <input type="date" value={scheduleForm.validTill} onChange={(e) => setScheduleForm({...scheduleForm, validTill: e.target.value})} className={validTillError ? 'error' : ''} />
                {validTillError && <span className="error-text">{validTillError}</span>}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => { setShowCreateSchedule(false); setScheduleSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreateSchedule}>Submit</button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreatePOModal = () => {
    const customerIdError = getPOError('customerId');
    const supplierIdError = getPOError('supplierId');
    const deliveryDateError = getPOError('deliveryDate');
    const selectedProductsError = getPOError('selectedProducts');

    return showCreatePO && (
      <div className="modal-overlay" onClick={() => { setShowCreatePO(false); setPoSubmitAttempted(false); }}>
        <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Create Purchase Order</h2>
            <button className="modal-close" onClick={() => { setShowCreatePO(false); setPoSubmitAttempted(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>PO Number</label>
                <input type="text" value={poForm.poNumber} disabled className="auto-field" />
                <small>Auto-generated</small>
              </div>
              <div className="form-group">
                <label>Order Date</label>
                <input type="date" value={poForm.orderDate} disabled className="auto-field" />
              </div>
              <div className="form-group">
                <label>Select Customer <span className="required-star">*</span></label>
                <select value={poForm.customerId} onChange={(e) => setPoForm({...poForm, customerId: e.target.value})} className={customerIdError ? 'error' : ''}>
                  <option value="">Select Customer</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                </select>
                {customerIdError && <span className="error-text">{customerIdError}</span>}
              </div>
              <div className="form-group">
                <label>Select Schedule <span className="optional-text">(Optional)</span></label>
                <select value={poForm.scheduleId} onChange={(e) => setPoForm({...poForm, scheduleId: e.target.value})}>
                  <option value="">Select Schedule</option>
                  {schedules.map(s => <option key={s.id} value={s.id}>{s.productName} - {s.customer}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Select Supplier <span className="required-star">*</span></label>
                <select value={poForm.supplierId} onChange={(e) => setPoForm({...poForm, supplierId: e.target.value})} className={supplierIdError ? 'error' : ''}>
                  <option value="">Select Supplier</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.companyName}</option>)}
                </select>
                {supplierIdError && <span className="error-text">{supplierIdError}</span>}
              </div>
              <div className="form-group">
                <label>Delivery Date <span className="required-star">*</span></label>
                <input type="date" value={poForm.deliveryDate} onChange={(e) => setPoForm({...poForm, deliveryDate: e.target.value})} className={deliveryDateError ? 'error' : ''} />
                {deliveryDateError && <span className="error-text">{deliveryDateError}</span>}
              </div>
            </div>

            <div className="select-products-section">
              <h4>Select Products <span className="required-star">*</span></h4>
              <select onChange={(e) => handleAddProductToPO(parseInt(e.target.value))} value="">
                <option value="">-- Add Product --</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name} - ₹{p.cost}</option>)}
              </select>
              {selectedProductsError && <span className="error-text">{selectedProductsError}</span>}

              {poForm.selectedProducts.length > 0 && (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr><th>Product</th><th>Quantity</th><th>Unit Price</th><th>Total</th><th>Action</th></tr>
                    </thead>
                    <tbody>
                      {poForm.selectedProducts.map(product => (
                        <tr key={product.id}>
                          <td>{product.name}</td>
                          <td><input type="number" value={product.quantity} onChange={(e) => handleUpdateProductQuantity(product.id, e.target.value, 'po')} style={{ width: '70px' }} /></td>
                          <td>₹{product.cost}</td>
                          <td>₹{(product.cost * product.quantity).toLocaleString()}</td>
                          <td><button className="action-btn delete" title="Remove" onClick={() => handleRemoveProduct('po', product.id)}><Trash2 size={16} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr><td colSpan="3"><strong>Total Amount</strong></td><td><strong>₹{poForm.totalAmount.toLocaleString()}</strong></td><td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => { setShowCreatePO(false); setPoSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreatePO}>Create PO</button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateSOModal = () => {
    const customerIdError = getSOError('customerId');
    const selectedProductsError = getSOError('selectedProducts');

    return showCreateSO && (
      <div className="modal-overlay" onClick={() => { setShowCreateSO(false); setSoSubmitAttempted(false); }}>
        <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Create Sale Order</h2>
            <button className="modal-close" onClick={() => { setShowCreateSO(false); setSoSubmitAttempted(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>SO Number</label>
                <input type="text" value={soForm.soNumber} disabled className="auto-field" />
                <small>Auto-generated</small>
              </div>
              <div className="form-group">
                <label>Order Date</label>
                <input type="date" value={soForm.orderDate} disabled className="auto-field" />
              </div>
              <div className="form-group">
                <label>Select Customer <span className="required-star">*</span></label>
                <select value={soForm.customerId} onChange={(e) => setSoForm({...soForm, customerId: e.target.value})} className={customerIdError ? 'error' : ''}>
                  <option value="">Select Customer</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                </select>
                {customerIdError && <span className="error-text">{customerIdError}</span>}
              </div>
              <div className="form-group">
                <label>Select Schedule <span className="optional-text">(Optional)</span></label>
                <select value={soForm.scheduleId} onChange={(e) => setSoForm({...soForm, scheduleId: e.target.value})}>
                  <option value="">Select Schedule</option>
                  {schedules.map(s => <option key={s.id} value={s.id}>{s.productName} - {s.customer}</option>)}
                </select>
              </div>
            </div>

            <div className="select-products-section">
              <h4>Select FG <span className="required-star">*</span></h4>
              <select onChange={(e) => handleAddProductToSO(parseInt(e.target.value))} value="">
                <option value="">-- Add Product --</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name} - Stock: {p.stock}</option>)}
              </select>
              {selectedProductsError && <span className="error-text">{selectedProductsError}</span>}

              {soForm.selectedProducts.length > 0 && (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr><th>Product</th><th>Quantity</th><th>Unit Price</th><th>Total</th><th>Action</th></tr>
                    </thead>
                    <tbody>
                      {soForm.selectedProducts.map(product => (
                        <tr key={product.id}>
                          <td>{product.name}</td>
                          <td><input type="number" value={product.quantity} onChange={(e) => handleUpdateProductQuantity(product.id, e.target.value, 'so')} style={{ width: '70px' }} /></td>
                          <td>₹{product.cost}</td>
                          <td>₹{(product.cost * product.quantity).toLocaleString()}</td>
                          <td><button className="action-btn delete" title="Remove" onClick={() => handleRemoveProduct('so', product.id)}><Trash2 size={16} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr><td colSpan="3"><strong>Total Amount</strong></td><td><strong>₹{soForm.totalAmount.toLocaleString()}</strong></td><td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => { setShowCreateSO(false); setSoSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreateSO}>Create Sale Order</button>
          </div>
        </div>
      </div>
    );
  };

  const renderSupplierPaymentModal = () => {
    const grnIdError = getPaymentError('grnId');
    const amountError = getPaymentError('amount');
    const utrNumberError = getPaymentError('utrNumber');

    return showSupplierPayment && (
      <div className="modal-overlay" onClick={() => { setShowSupplierPayment(false); setSupplierPaymentSubmitAttempted(false); }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Supplier Payment</h2>
            <button className="modal-close" onClick={() => { setShowSupplierPayment(false); setSupplierPaymentSubmitAttempted(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Payment ID</label>
                <input type="text" value={paymentForm.paymentId} disabled className="auto-field" />
                <small>Auto-generated</small>
              </div>
              <div className="form-group">
                <label>Payment Date</label>
                <input type="date" value={paymentForm.paymentDate} onChange={(e) => setPaymentForm({...paymentForm, paymentDate: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Select GRN <span className="required-star">*</span></label>
                <select value={paymentForm.grnId} onChange={(e) => setPaymentForm({...paymentForm, grnId: e.target.value})} className={grnIdError ? 'error' : ''}>
                  <option value="">Select GRN</option>
                  {grns.map(g => <option key={g.id} value={g.grnNumber}>{g.grnNumber} - {g.supplier} - ₹{g.quantity * 1000}</option>)}
                </select>
                {grnIdError && <span className="error-text">{grnIdError}</span>}
              </div>
              <div className="form-group">
                <label>Amount <span className="required-star">*</span></label>
                <input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value)})} className={amountError ? 'error' : ''} placeholder="Enter amount" />
                {amountError && <span className="error-text">{amountError}</span>}
              </div>
              <div className="form-group">
                <label>Payment Mode <span className="required-star">*</span></label>
                <select value={paymentForm.paymentMode} onChange={(e) => setPaymentForm({...paymentForm, paymentMode: e.target.value})}>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Cash">Cash</option>
                </select>
              </div>
              <div className="form-group">
                <label>UTR Number <span className="required-star">*</span></label>
                <input type="text" value={paymentForm.utrNumber} onChange={(e) => setPaymentForm({...paymentForm, utrNumber: e.target.value})} className={utrNumberError ? 'error' : ''} placeholder="Enter UTR number" />
                {utrNumberError && <span className="error-text">{utrNumberError}</span>}
              </div>
              <div className="form-group">
                <label>Verified</label>
                <input type="checkbox" checked={paymentForm.verified} onChange={(e) => setPaymentForm({...paymentForm, verified: e.target.checked})} style={{ width: 'auto' }} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => { setShowSupplierPayment(false); setSupplierPaymentSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleSupplierPayment}>Submit Payment</button>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomerPaymentModal = () => {
    const invoiceIdError = getCustomerPaymentError('invoiceId');
    const amountError = getCustomerPaymentError('amount');
    const utrNumberError = getCustomerPaymentError('utrNumber');

    return showCustomerPayment && (
      <div className="modal-overlay" onClick={() => { setShowCustomerPayment(false); setCustomerPaymentSubmitAttempted(false); }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Customer Invoice Payment</h2>
            <button className="modal-close" onClick={() => { setShowCustomerPayment(false); setCustomerPaymentSubmitAttempted(false); }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Payment ID</label>
                <input type="text" value={customerPaymentForm.paymentId} disabled className="auto-field" />
                <small>Auto-generated</small>
              </div>
              <div className="form-group">
                <label>Payment Date</label>
                <input type="date" value={customerPaymentForm.paymentDate} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, paymentDate: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Select Invoice <span className="required-star">*</span></label>
                <select value={customerPaymentForm.invoiceId} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, invoiceId: e.target.value})} className={invoiceIdError ? 'error' : ''}>
                  <option value="">Select Invoice</option>
                  {invoices.map(inv => <option key={inv.id} value={inv.id}>{inv.invoiceNumber} - {inv.customer} - ₹{inv.amount}</option>)}
                </select>
                {invoiceIdError && <span className="error-text">{invoiceIdError}</span>}
              </div>
              <div className="form-group">
                <label>Amount <span className="required-star">*</span></label>
                <input type="number" value={customerPaymentForm.amount} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, amount: parseFloat(e.target.value)})} className={amountError ? 'error' : ''} placeholder="Enter amount" />
                {amountError && <span className="error-text">{amountError}</span>}
              </div>
              <div className="form-group">
                <label>Payment Mode <span className="required-star">*</span></label>
                <select value={customerPaymentForm.paymentMode} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, paymentMode: e.target.value})}>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Cash">Cash</option>
                </select>
              </div>
              <div className="form-group">
                <label>UTR Number <span className="required-star">*</span></label>
                <input type="text" value={customerPaymentForm.utrNumber} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, utrNumber: e.target.value})} className={utrNumberError ? 'error' : ''} placeholder="Enter UTR number" />
                {utrNumberError && <span className="error-text">{utrNumberError}</span>}
              </div>
              <div className="form-group">
                <label>CGST (%) <span className="optional-text">(Optional)</span></label>
                <input type="number" value={customerPaymentForm.cgst} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, cgst: parseFloat(e.target.value)})} />
              </div>
              <div className="form-group">
                <label>SGST (%) <span className="optional-text">(Optional)</span></label>
                <input type="number" value={customerPaymentForm.sgst} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, sgst: parseFloat(e.target.value)})} />
              </div>
              <div className="form-group">
                <label>IGST (%) <span className="optional-text">(Optional)</span></label>
                <input type="number" value={customerPaymentForm.igst} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, igst: parseFloat(e.target.value)})} />
              </div>
              <div className="form-group">
                <label>Verified</label>
                <input type="checkbox" checked={customerPaymentForm.verified} onChange={(e) => setCustomerPaymentForm({...customerPaymentForm, verified: e.target.checked})} style={{ width: 'auto' }} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => { setShowCustomerPayment(false); setCustomerPaymentSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleCustomerPayment}>Submit Payment</button>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch(activeTab) {
      case "create-ledger": return renderCreateLedger();
      case "product": return renderProduct();
      case "create-schedule": return renderCreateSchedule();
      case "create-po": return renderCreatePO();
      case "list-po": return renderListPO();
      case "create-sale-order": return renderCreateSaleOrder();
      case "list-sale-order": return renderListSaleOrder();
      case "dispatch": return renderDispatch();
      case "list-grn": return renderListGRN();
      case "list-invoices": return renderListInvoices();
      case "supplier-payment": return renderSupplierPayment();
      case "customer-payment": return renderCustomerPayment();
      default: return renderCreateLedger();
    }
  };

  return (
    <div className="store-dashboard-container">
      {/* Sidebar */}
      <div className="store-sidebar">
        <div className="store-logo-container">
          <div className="store-logo-icon">
            <img src="/rahulerp.jpg" alt="Logo" className="store-logo-image" onError={e => { e.target.src = 'https://via.placeholder.com/48x48?text=RAT'; }} />
          </div>
          <div>
            <h2 className="store-company-name">Ragul Auto Tech</h2>
            <p className="store-company-tagline">AI Powered ERP</p>
          </div>
        </div>
        <nav className="store-nav-menu">
          {tabItems.map(tab => (
            <button
              key={tab.id}
              className={`store-nav-item ${activeTab === tab.id ? 'store-nav-item-active' : ''}`}
              onClick={() => { setActiveTab(tab.id); handleBack(); }}
            >
              <span className="store-nav-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
        <div className="store-logout-container">
          <button onClick={handleLogoutClick} className="store-logout-button"><LogOut size={18} /> Logout</button>
        </div>
      </div>

      {/* Main Content */}
      <div className="store-main-content">
        <ScreenHeader
          title="Store Dashboard"
          description={`Welcome, ${user?.role || 'Store Manager'}`}
          timestamp={new Date().toLocaleString()}
          containerClassName="store-header"
          titleClassName="store-main-title"
          descriptionClassName="store-subtitle"
          actionsClassName="store-header-actions"
          timestampClassName="store-last-updated"
        />

        {/* Back to MD Button */}
        {cameFromMDDashboard && (
          <div className="store-back-button-wrapper">
            <button onClick={handleBackToMD} className="btn-edit" style={{ background: "linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)" }}>
              <ArrowLeft size={18} /> Back to MD Dashboard
            </button>
          </div>
        )}

        {/* Scrollable Content */}
        <div className="store-scrollable-content">
          <div className="store-content-card">
            {renderContent()}
          </div>
        </div>

        {/* Logout Modal */}
        {showLogoutModal && (
          <div className="logout-modal-overlay">
            <div className="logout-modal">
              <div className="logout-modal-header"><div className="logout-modal-icon">👋</div><h3>Confirm Logout</h3></div>
              <div className="logout-modal-body"><p>Are you sure you want to logout from <strong>Store</strong> dashboard?</p><p className="logout-modal-warning">You will be redirected to the login page.</p></div>
              <div className="logout-modal-footer"><button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button><button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button></div>
            </div>
          </div>
        )}

        {/* All Modals */}
        {renderCreateCustomerModal()}
        {renderCreateSupplierModal()}
        {renderCreateCategoryModal()}
        {renderCreateProductModal()}
        {renderCreateGroupModal()}
        {renderCreateScheduleModal()}
        {renderCreatePOModal()}
        {renderCreateSOModal()}
        {renderSupplierPaymentModal()}
        {renderCustomerPaymentModal()}
      </div>
    </div>
  );
}
              